import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { JournalPanel } from "./components/JournalPanel";
import { MediaGalleryPanel } from "./components/MediaGalleryPanel";
import { FamilyConfessionsPanel } from "./components/FamilyConfessionsPanel";
import { WillPreparationPanel } from "./components/WillPreparationPanel";
import { MyProfilePanel } from "./components/MyProfilePanel";
import { PaymentHistoryPanel } from "./components/PaymentHistoryPanel";
import { TrustedPeopleBuilderPanel } from "./components/TrustedPeopleBuilderPanel";
import { AdminOperationsPage } from "./components/admin/AdminOperationsPage";
import { HeroRotator } from "./components/HeroRotator";
import { ProductHeroShowcase } from "./components/ProductHeroShowcase";
import { HowItWorksPage } from "./components/HowItWorksPage";
import { SecurityPrivacyPage } from "./components/SecurityPrivacyPage";
import { SiteFooter } from "./components/SiteFooter";
import { TermsOfServicePage } from "./components/TermsOfServicePage";
import { CookieConsent } from "./components/CookieConsent";
import { AfterMeGuideChat } from "./components/AfterMeGuideChat";
import { GuestContactWidget } from "./components/GuestContactWidget";
import { getPageMetadata, getPublicSiteCopy } from "./content/publicSiteLocale";
import { getAppCopy } from "./content/appLocale";
import { getApiBase } from "./apiBase";
import "./App.css";

const STORAGE_GB_LIMIT = 2;

function formatStorageBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) {
    return `${Math.round(n)} B`;
  }
  if (n < 1024 * 1024) {
    return `${(n / 1024).toFixed(1)} KB`;
  }
  if (n < 1024 * 1024 * 1024) {
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  }
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatFriendlyDate(iso, locale = "en") {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return new Intl.DateTimeFormat(locale === "fr" ? "fr-CA" : "en-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

/** Matches server `lib/subscriptionPlans.js` INTRO_TRIAL_MONTHS for preview dates on the plan page. */
const PLAN_INTRO_TRIAL_MONTHS = 3;

function addMonthsPreview(iso, months) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  date.setMonth(date.getMonth() + months);
  return date.toISOString();
}

const API_BASE = getApiBase();

/** JWT `exp` in ms for client-side session UI (server still verifies tokens). */
function parseJwtExpMs(token) {
  if (!token || typeof token !== "string") {
    return null;
  }
  const parts = token.split(".");
  if (parts.length < 2) {
    return null;
  }
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, "=");
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );
    const payload = JSON.parse(json);
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Show warning when this many ms remain (3 min left in a 15 min session). */
const SESSION_WARN_WHEN_REMAINING_MS = 3 * 60 * 1000;
const SESSION_CHECK_INTERVAL_MS = 10_000;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const HOME_VIDEO_URL = (process.env.REACT_APP_HOME_VIDEO_URL || "").trim();
const LS_LOCALE = "afterme_locale";
const EMPTY_UPGRADE_REMINDER = {
  loaded: false,
  planTier: "free",
  isFreePlan: false,
  intervalMinutes: 20,
  lastShownAt: "",
  lastDismissedAt: "",
  lastOpenedPricingAt: "",
  shownCount: 0,
  nextEligibleAt: "",
  canPromptFromHistory: false,
};
const APP_FAMILY_COPY = {
  en: {
    createWillCard: {
      title: "Create a Will",
      description:
        "Organize draft planning notes, executor preferences, assets, liabilities, and digital legacy details for lawyer review.",
      cta: "Open will toolkit →",
      ctaOpen: "Will toolkit open below — click again to close",
    },
    createWillPage: {
      title: "Will Preparation Toolkit",
      body:
        "Organize draft planning notes, family context, executor preferences, assets, liabilities, and digital legacy details for lawyer review.",
      asideTitle: "Not legal advice",
      asidePoints: [
        "AfterMe does not provide legal, tax, medical, or financial advice.",
        "Do not enter passwords, seed phrases, private keys, SIN, or full account numbers.",
        "You may choose to request lawyer review as a paid next step later.",
      ],
      backToDashboard: "Back to dashboard",
    },
  },
  fr: {
    createWillCard: {
      title: "Créer un testament",
      description:
        "Organisez des notes préparatoires, les préférences d’exécuteur, les actifs, les dettes et les détails de patrimoine numérique pour une révision par un avocat.",
      cta: "Ouvrir la trousse testamentaire →",
      ctaOpen: "Trousse testamentaire ouverte ci-dessous — cliquez à nouveau pour fermer",
    },
    createWillPage: {
      title: "Trousse de préparation testamentaire",
      body:
        "Organisez des notes préparatoires, le contexte familial, les préférences d’exécuteur, les actifs, les dettes et les détails de patrimoine numérique pour une révision par un avocat.",
      asideTitle: "Pas un avis juridique",
      asidePoints: [
        "AfterMe ne fournit pas de conseils juridiques, fiscaux, médicaux ou financiers.",
        "N’entrez pas de mots de passe, phrases de récupération, clés privées, NAS ou numéros de compte complets.",
        "Vous pourrez plus tard demander une révision par un avocat comme prochaine étape payante.",
      ],
      backToDashboard: "Retour au tableau de bord",
    },
  },
};

function buildFeatureCards(appCopy, createWillCopy) {
  return [
    {
      id: "private-journals",
      title: appCopy.featureCards.privateJournals.title,
      description: appCopy.featureCards.privateJournals.description,
    },
    {
      id: "family-confessions",
      title: appCopy.featureCards.familyConfessions.title,
      description: appCopy.featureCards.familyConfessions.description,
    },
    {
      id: "photo-memory-collage",
      title: appCopy.featureCards.photoMemoryCollage.title,
      description: appCopy.featureCards.photoMemoryCollage.description,
    },
    {
      id: "create-will",
      title: createWillCopy.title,
      description: createWillCopy.description,
    },
    {
      id: "posthumous-release-workflow",
      title: appCopy.featureCards.posthumousReleaseWorkflow.title,
      description: appCopy.featureCards.posthumousReleaseWorkflow.description,
    },
  ];
}

const CHART_CARD_W = 206;
const CHART_CARD_H = 108;
const CHART_ROW_GAP = 130;
const CHART_GAP_X = 34;
const CHART_CANVAS_MIN_W = 860;
const CHART_CANVAS_PADDING_X = 36;

function buildChartBranchPath(from, to) {
  const midY = from.bottom + Math.max(28, (to.top - from.bottom) * 0.48);
  return `M ${from.cx} ${from.bottom} C ${from.cx} ${midY}, ${to.cx} ${midY}, ${to.cx} ${to.top}`;
}

function chartMemberInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (!parts.length) {
    return "?";
  }
  return parts.map((part) => part.charAt(0).toUpperCase()).join("");
}

function chartMemberTone(member) {
  if (!member) {
    return "relative";
  }
  if (member.relation === "Owner") {
    return "owner";
  }
  if (member.relation === "Spouse" || member.relation === "Partner") {
    return "partner";
  }
  if (member.relation === "Child" || member.relation === "Grandchild") {
    return "child";
  }
  return "relative";
}

function isPartnerRelation(member) {
  return member && (member.relation === "Spouse" || member.relation === "Partner");
}

function chartMemberAccessLabel(member, copy) {
  if (!member) {
    return "";
  }
  if (member.relation === "Owner") {
    return copy.accountHolder;
  }
  if (member.inviteStatus === "existing-user") {
    return copy.alreadyUsing;
  }
  if (member.inviteStatus === "invite-requested") {
    return copy.inviteRequested;
  }
  if (member.email) {
    return copy.accessReady;
  }
  return copy.noEmailYet;
}

function appRelationLabel(relation, trustedCopy) {
  const map = {
    Owner: trustedCopy.ownerRelation,
    Parent: trustedCopy.relationshipOptions[0],
    Spouse: trustedCopy.relationshipOptions[1],
    Partner: trustedCopy.relationshipOptions[2],
    "Ex-spouse": trustedCopy.relationshipOptions[3],
    Sibling: trustedCopy.relationshipOptions[4],
    Child: trustedCopy.relationshipOptions[5],
    Grandchild: trustedCopy.relationshipOptions[6],
    Friend: trustedCopy.relationshipOptions[7],
    "Legal Advisor": trustedCopy.relationshipOptions[8],
  };
  return map[relation] || relation;
}

/** Dynamo stores `parentNodeId`; UI uses `chartParentId` after normalization. */
function chartParentKeyFromMember(m) {
  if (!m || m.relation === "Owner") {
    return null;
  }
  const raw = m.chartParentId != null ? m.chartParentId : m.parentNodeId;
  if (raw == null || raw === "" || raw === "root") {
    return "owner-node";
  }
  return String(raw);
}

/** Hierarchy: owner (and linked spouse on the same row), then children/grandchildren under parents. */
function computeFamilyChartPositions(members) {
  const pos = {};
  const owner = members.find((x) => x.relation === "Owner");
  if (!owner) {
    return pos;
  }
  const placed = new Set();
  const sortByName = (a, b) => String(a.name).localeCompare(String(b.name));
  const partnerMap = new Map();
  members.forEach((member) => {
    if (!isPartnerRelation(member) || !member.partnerNodeId) {
      return;
    }
    const anchorKey = member.partnerNodeId;
    const list = partnerMap.get(anchorKey) || [];
    list.push(member);
    partnerMap.set(anchorKey, list);
  });
  partnerMap.forEach((list, key) => {
    partnerMap.set(key, list.sort(sortByName));
  });

  const reserveWidthFor = (anchorMember) => {
    const anchorKey =
      anchorMember.relation === "Owner" ? "owner-node" : anchorMember.backendNodeId || null;
    const linkedPartners = anchorKey ? partnerMap.get(anchorKey) || [] : [];
    return CHART_CARD_W + (linkedPartners.length ? CHART_CARD_W + 18 : 0);
  };

  const placePartnersForAnchor = (anchorMember, anchorX, anchorY) => {
    const anchorKey =
      anchorMember.relation === "Owner" ? "owner-node" : anchorMember.backendNodeId || null;
    const linkedPartners = anchorKey ? partnerMap.get(anchorKey) || [] : [];
    linkedPartners.forEach((member, index) => {
      if (placed.has(member.id)) {
        return;
      }
      pos[member.id] = {
        x: anchorX + CHART_CARD_W + 18,
        y: anchorY + index * 12,
      };
      placed.add(member.id);
    });
  };

  const placeRow = (anchors, y, centerX, gap = CHART_GAP_X) => {
    if (!anchors.length) {
      return;
    }
    const widths = anchors.map((anchor) => reserveWidthFor(anchor));
    const totalWidth = widths.reduce((sum, width) => sum + width, 0) + gap * (anchors.length - 1);
    let cursor = centerX - totalWidth / 2;
    anchors.forEach((anchor, index) => {
      pos[anchor.id] = { x: cursor, y };
      placed.add(anchor.id);
      placePartnersForAnchor(anchor, cursor, y);
      cursor += widths[index] + gap;
    });
  };

  const centerX = 388;
  const parentRowY = 42;
  const ownerRowY = parentRowY + CHART_ROW_GAP;
  const childRowY = ownerRowY + CHART_ROW_GAP;
  const grandchildRowY = childRowY + CHART_ROW_GAP;
  const overflowRowY = grandchildRowY + CHART_ROW_GAP;

  const parents = members.filter((member) => member.relation === "Parent").sort(sortByName);
  const siblings = members.filter((member) => member.relation === "Sibling").sort(sortByName);
  const children = members.filter((member) => member.relation === "Child").sort(sortByName);
  const grandchildren = members.filter((member) => member.relation === "Grandchild").sort(sortByName);

  placeRow(parents, parentRowY, centerX, 24);

  const half = Math.ceil(siblings.length / 2);
  const sameGenerationAnchors = [
    ...siblings.slice(0, half),
    owner,
    ...siblings.slice(half),
  ];
  placeRow(sameGenerationAnchors, ownerRowY, centerX, 24);

  placeRow(children, childRowY, centerX, 28);

  const childPositions = Object.fromEntries(
    children
      .filter((child) => pos[child.id])
      .map((child) => [child.backendNodeId || child.id, pos[child.id]])
  );
  const grandchildrenByParent = new Map();
  grandchildren.forEach((grandchild) => {
    const parentKey = chartParentKeyFromMember(grandchild);
    const list = grandchildrenByParent.get(parentKey) || [];
    list.push(grandchild);
    grandchildrenByParent.set(parentKey, list);
  });
  grandchildrenByParent.forEach((list, parentKey) => {
    const parentPoint = childPositions[parentKey];
    if (!parentPoint) {
      return;
    }
    const ordered = list.sort(sortByName);
    const spread = ordered.length <= 1 ? 0 : Math.min(236, 112 + (ordered.length - 1) * 48);
    ordered.forEach((member, index) => {
      const cx =
        parentPoint.x + CHART_CARD_W / 2 + (index - (ordered.length - 1) / 2) * spread;
      pos[member.id] = { x: cx - CHART_CARD_W / 2, y: grandchildRowY };
      placed.add(member.id);
      placePartnersForAnchor(member, cx - CHART_CARD_W / 2, grandchildRowY);
    });
  });

  members.forEach((m, i) => {
    if (m.relation === "Owner" || placed.has(m.id) || isPartnerRelation(m)) {
      return;
    }
    pos[m.id] = { x: 42 + (i % 3) * 246, y: overflowRowY + Math.floor(i / 3) * 136 };
    placed.add(m.id);
  });
  const entries = Object.entries(pos);
  let minX = Number.POSITIVE_INFINITY;
  entries.forEach(([, point]) => {
    if (point && typeof point.x === "number") {
      minX = Math.min(minX, point.x);
    }
  });
  if (!Number.isFinite(minX) || minX >= CHART_CANVAS_PADDING_X) {
    return pos;
  }
  const shiftX = CHART_CANVAS_PADDING_X - minX;
  return Object.fromEntries(
    entries.map(([id, point]) => [id, { ...point, x: point.x + shiftX }])
  );
}

/** Intro pricing copy for the public pricing page. */
function PlanPricePromo({ amount, periodLabel, planPrice }) {
  const fullLabel = `${amount}${periodLabel}`;
  const pp = planPrice || {
    introLabel: "Start now with introductory access",
    futurePrefix: "Later",
    futureAria: "Start now with introductory access. Later {fullLabel}.",
    futureCaption: "No surprise billing or automatic charges.",
  };
  const futureAria = pp.futureAria.replace(/\{fullLabel\}/g, fullLabel);
  return (
    <div className="plan-price-stack">
      <p className="plan-price-promo-primary">{pp.introLabel}</p>
      <p className="plan-price-list-row" aria-label={futureAria}>
        <span className="plan-price-future-prefix">{pp.futurePrefix}</span>
        <span className="plan-price-amount">{amount}</span>
        <span className="plan-price-period">{periodLabel}</span>
      </p>
      <p className="plan-price-after-intro">{pp.futureCaption}</p>
    </div>
  );
}

const LS_TOKEN = "afterme_token";
const LS_USER = "afterme_user";
const LS_SELECTED_PLAN = "afterme_selected_plan";

function readStoredLocale() {
  try {
    const url = new URL(window.location.href);
    const fromUrl = String(url.searchParams.get("lang") || "")
      .trim()
      .toLowerCase();
    if (fromUrl === "fr" || fromUrl === "en") {
      return fromUrl;
    }
  } catch {
    /* ignore */
  }
  try {
    const stored = String(localStorage.getItem(LS_LOCALE) || "")
      .trim()
      .toLowerCase();
    if (stored === "fr" || stored === "en") {
      return stored;
    }
  } catch {
    /* ignore */
  }
  try {
    const browserLocale = String(navigator.language || navigator.languages?.[0] || "").toLowerCase();
    if (browserLocale.startsWith("fr")) {
      return "fr";
    }
  } catch {
    /* ignore */
  }
  return "en";
}

function writeStoredLocale(nextLocale) {
  try {
    localStorage.setItem(LS_LOCALE, nextLocale);
  } catch {
    /* ignore */
  }
}

function persistSession(token, user) {
  try {
    localStorage.setItem(LS_TOKEN, token);
    localStorage.setItem(LS_USER, JSON.stringify(user));
  } catch {
    /* ignore quota / private mode */
  }
}

function readStoredSession() {
  try {
    const token = localStorage.getItem(LS_TOKEN);
    if (!token) {
      return null;
    }
    const raw = localStorage.getItem(LS_USER);
    const user = raw ? JSON.parse(raw) : null;
    return { token, user };
  } catch {
    return null;
  }
}

function clearStoredSession() {
  try {
    localStorage.removeItem(LS_TOKEN);
    localStorage.removeItem(LS_USER);
  } catch {
    /* ignore */
  }
}

function readStoredSelectedPlan() {
  try {
    return String(sessionStorage.getItem(LS_SELECTED_PLAN) || "").trim().toLowerCase();
  } catch {
    return "";
  }
}

function persistSelectedPlan(planId) {
  try {
    if (!planId) {
      sessionStorage.removeItem(LS_SELECTED_PLAN);
      return;
    }
    sessionStorage.setItem(LS_SELECTED_PLAN, String(planId).trim().toLowerCase());
  } catch {
    /* ignore */
  }
}

const PUBLIC_VIEW_PARAM = {
  how: "how-it-works",
  security: "security",
  plan: "pricing",
  terms: "terms",
};

function readPublicViewFromSearch(search) {
  try {
    const params = new URLSearchParams(search);
    const truthy = (value) => value === "1" || value === "true" || value === "yes";
    if (truthy(params.get("plan"))) {
      return "plan";
    }
    if (truthy(params.get("terms"))) {
      return "terms";
    }
    const page = String(params.get("page") || "").trim().toLowerCase();
    if (page === "how-it-works") {
      return "how";
    }
    if (page === "security") {
      return "security";
    }
    if (page === "pricing") {
      return "plan";
    }
    if (page === "terms") {
      return "terms";
    }
  } catch {
    /* ignore */
  }
  return "home";
}

function writePublicViewToUrl(nextView, historyMode = "push") {
  try {
    const url = new URL(window.location.href);
    ["plan", "terms", "page"].forEach((key) => url.searchParams.delete(key));
    const pageParam = PUBLIC_VIEW_PARAM[nextView];
    if (pageParam) {
      url.searchParams.set("page", pageParam);
    }
    const q = url.searchParams.toString();
    const fn = historyMode === "replace" ? "replaceState" : "pushState";
    window.history[fn]({}, "", `${url.pathname}${q ? `?${q}` : ""}${url.hash}`);
  } catch {
    /* ignore */
  }
}

function writeLocaleToUrl(nextLocale, historyMode = "replace") {
  try {
    const url = new URL(window.location.href);
    if (nextLocale === "fr") {
      url.searchParams.set("lang", "fr");
    } else {
      url.searchParams.delete("lang");
    }
    const q = url.searchParams.toString();
    const fn = historyMode === "replace" ? "replaceState" : "pushState";
    window.history[fn]({}, "", `${url.pathname}${q ? `?${q}` : ""}${url.hash}`);
  } catch {
    /* ignore */
  }
}

/** Removes shareable SPA query keys from the URL without navigation. */
function stripShareablePageParams() {
  try {
    const url = new URL(window.location.href);
    let changed = false;
    ["plan", "terms", "page"].forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    });
    if (!changed) {
      return;
    }
    const q = url.searchParams.toString();
    window.history.replaceState({}, "", `${url.pathname}${q ? `?${q}` : ""}${url.hash}`);
  } catch {
    /* ignore */
  }
}

function App() {
  const [view, setView] = useState("home");
  const [locale, setLocale] = useState(() => readStoredLocale());
  const [activeSection, setActiveSection] = useState("overview");
  const [authMode, setAuthMode] = useState("login");
  const [authToken, setAuthToken] = useState("");
  const [authUser, setAuthUser] = useState(null);
  const [authForm, setAuthForm] = useState({ name: "", email: "", password: "" });
  const [authError, setAuthError] = useState("");
  const [googleBusy, setGoogleBusy] = useState(false);
  const [magicLinkBusy, setMagicLinkBusy] = useState(false);
  const [magicLinkHint, setMagicLinkHint] = useState("");
  const [magicLinkDevUrl, setMagicLinkDevUrl] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [resetPasswordForm, setResetPasswordForm] = useState({
    password: "",
    confirm: "",
  });
  const [forgotHint, setForgotHint] = useState("");
  const [forgotDevUrl, setForgotDevUrl] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [authSuccessBanner, setAuthSuccessBanner] = useState("");
  const [sessionWarningOpen, setSessionWarningOpen] = useState(false);
  const [sessionExtendBusy, setSessionExtendBusy] = useState(false);
  const [sessionExtendError, setSessionExtendError] = useState("");
  const [sessionClock, setSessionClock] = useState(0);
  const [memberName, setMemberName] = useState("");
  const [memberEmail, setMemberEmail] = useState("");
  const [memberRelation, setMemberRelation] = useState("Parent");
  const [memberNickname, setMemberNickname] = useState("");
  const [memberPronouns, setMemberPronouns] = useState("");
  const [memberBirthYear, setMemberBirthYear] = useState("");
  const [memberCoParentId, setMemberCoParentId] = useState("");
  const [memberPartnerId, setMemberPartnerId] = useState("owner-node");
  const [memberChartParentId, setMemberChartParentId] = useState("");
  const [memberIdentityNote, setMemberIdentityNote] = useState("");
  const [memberSpecialNote, setMemberSpecialNote] = useState("");
  const [familyMembers, setFamilyMembers] = useState([]);
  const [familyInvitations, setFamilyInvitations] = useState([]);
  const [editingFamilyEmailNodeId, setEditingFamilyEmailNodeId] = useState("");
  const [editingFamilyEmailValue, setEditingFamilyEmailValue] = useState("");
  const [familyEmailSavingId, setFamilyEmailSavingId] = useState("");
  const [familyInviteBusyId, setFamilyInviteBusyId] = useState("");
  const [familyInvitationBusyId, setFamilyInvitationBusyId] = useState("");
  /** `{ nodeId, name }` when confirming removal of a family chart member (not Owner). */
  const [familyDeleteConfirm, setFamilyDeleteConfirm] = useState(null);
  const [familyDeleting, setFamilyDeleting] = useState(false);
  const [familyPlanLimitPrompt, setFamilyPlanLimitPrompt] = useState(null);
  /** Bumped after family member removal so media gallery refreshes tags / folders. */
  const [mediaGalleryTick, setMediaGalleryTick] = useState(0);
  const [selectedRecipient, setSelectedRecipient] = useState("all-family");
  const [mediaStats, setMediaStats] = useState({
    totalBytes: 0,
    totalCount: 0,
    quotaBytes: STORAGE_GB_LIMIT * 1024 * 1024 * 1024,
    mediaAvailable: true,
    notice: "",
    loaded: false,
  });
  const [verificationId, setVerificationId] = useState("identity");
  /** Which feature tile workspace is open below the cards (null = none, shorter page). */
  const [selectedFeatureCard, setSelectedFeatureCard] = useState(null);
  const [requestedJournalId, setRequestedJournalId] = useState("");
  /** Signed-in area: dashboard vs dedicated family, media, or verification page. */
  const [appPage, setAppPage] = useState("dashboard");
  const [adminSession, setAdminSession] = useState({
    checked: false,
    configured: false,
    canAccess: false,
    email: "",
  });
  const [selectedPlanId, setSelectedPlanId] = useState(() => readStoredSelectedPlan());
  const [planEnrollmentBusy, setPlanEnrollmentBusy] = useState(false);
  const [planEnrollmentError, setPlanEnrollmentError] = useState("");
  const [planEnrollmentResult, setPlanEnrollmentResult] = useState(null);
  const [planEntrySource, setPlanEntrySource] = useState("");

  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [hoveredChartMemberId, setHoveredChartMemberId] = useState(null);
  const [nodePositions, setNodePositions] = useState({});
  const familyChartDefaultPositions = useMemo(
    () => computeFamilyChartPositions(familyMembers),
    [familyMembers]
  );
  const familyChartViewHeight = useMemo(() => {
    let maxBottom = 400;
    const ids = new Set([
      ...Object.keys(familyChartDefaultPositions),
      ...Object.keys(nodePositions),
    ]);
    ids.forEach((id) => {
      const p = nodePositions[id] ?? familyChartDefaultPositions[id];
      if (p && typeof p.y === "number") {
        maxBottom = Math.max(maxBottom, p.y + CHART_CARD_H + 52);
      }
    });
    return maxBottom;
  }, [familyChartDefaultPositions, nodePositions]);
  const familyChartViewWidth = useMemo(() => {
    let maxRight = CHART_CANVAS_MIN_W;
    const ids = new Set([
      ...Object.keys(familyChartDefaultPositions),
      ...Object.keys(nodePositions),
    ]);
    ids.forEach((id) => {
      const p = nodePositions[id] ?? familyChartDefaultPositions[id];
      if (p && typeof p.x === "number") {
        maxRight = Math.max(maxRight, p.x + CHART_CARD_W + CHART_CANVAS_PADDING_X);
      }
    });
    return maxRight;
  }, [familyChartDefaultPositions, nodePositions]);
  const activeChartMember = useMemo(() => {
    const selected = familyMembers.find((member) => member.id === selectedNodeId);
    if (selected) {
      return selected;
    }
    return familyMembers.find((member) => member.relation === "Owner") || familyMembers[0] || null;
  }, [familyMembers, selectedNodeId]);
  const dragStateRef = useRef(null);
  const svgRef = useRef(null);
  const accountMenuRef = useRef(null);
  const authTokenRef = useRef("");

  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirm: "",
  });
  const [changePasswordBusy, setChangePasswordBusy] = useState(false);
  const [changePasswordMessage, setChangePasswordMessage] = useState("");
  const [changePasswordFeedbackError, setChangePasswordFeedbackError] = useState(false);
  const [appNotice, setAppNotice] = useState("");
  const [upgradeReminder, setUpgradeReminder] = useState(EMPTY_UPGRADE_REMINDER);
  const [upgradeReminderOpen, setUpgradeReminderOpen] = useState(false);
  const [signedInSessionStartedAt, setSignedInSessionStartedAt] = useState(0);
  const isSignedIn = Boolean(authToken);

  const sectionIds = useMemo(() => ["overview", "journal", "features"], []);
  const sectionRefs = useRef({});
  const setSectionRef = (id) => (node) => {
    if (node) {
      sectionRefs.current[id] = node;
    }
  };

  const scrollToSection = useCallback((id) => {
    const node = sectionRefs.current[id] || document.getElementById(id);
    if (!node) {
      return;
    }
    setActiveSection(id);
    node.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const openPhotoMemoryGallery = () => {
    stripShareablePageParams();
    setView("app");
    setAppPage("dashboard");
    setSelectedFeatureCard("photo-memory-collage");
    scrollToSection("journal");
  };

  const openFamilyChartGuideTarget = useCallback(() => {
    stripShareablePageParams();
    setView("app");
    setAppPage("family");
    setActiveSection("family");
  }, []);

  const openJournalGuideTarget = useCallback(() => {
    stripShareablePageParams();
    setView("app");
    setAppPage("dashboard");
    setSelectedFeatureCard("private-journals");
    setRequestedJournalId("");
    scrollToSection("journal");
  }, [scrollToSection]);

  const openVerificationGuideTarget = useCallback(() => {
    stripShareablePageParams();
    setView("app");
    setAppPage("verification");
    setActiveSection("verification");
  }, []);

  const photoGalleryNavActive =
    appPage === "dashboard" &&
    activeSection === "journal" &&
    selectedFeatureCard === "photo-memory-collage";

  const childBranchOptions = useMemo(
    () =>
      familyMembers.filter(
        (m) =>
          m.relation === "Child" &&
          m.backendNodeId &&
          m.backendNodeId !== "owner-node"
      ),
    [familyMembers]
  );

  const partnerLinkOptions = useMemo(
    () =>
      familyMembers.filter(
        (m) =>
          m.relation !== "Owner" &&
          m.backendNodeId &&
          m.backendNodeId !== "owner-node"
      ),
    [familyMembers]
  );

  const loadFamilyTree = async (token, sessionUser = null) => {
    const ownerName = sessionUser?.name ?? authUser?.name ?? "You";
    const response = await fetch(`${API_BASE}/api/family-tree`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      throw new Error("Unable to load family tree.");
    }

    const nodes = await response.json();
    const owner = {
      id: "owner-node",
      backendNodeId: "owner-node",
      name: ownerName,
      relation: "Owner",
      depth: 0,
    };
    const mapped = nodes.map((node) => ({
      id: `node-${node.nodeId}`,
      backendNodeId: node.nodeId,
      name: node.name,
      relation: node.relation,
      email: node.email || "",
      nickname: node.nickname || "",
      pronouns: node.pronouns || "",
      identityNote: node.identityNote || "",
      specialNote: node.specialNote || "",
      birthYear: node.birthYear != null && node.birthYear !== "" ? Number(node.birthYear) : null,
      legacyAge: node.age != null && node.age !== "" ? Number(node.age) : null,
      coParentNodeId: node.coParentNodeId || null,
      partnerNodeId:
        node.partnerNodeId ||
        ((node.relation === "Spouse" || node.relation === "Partner") &&
        (node.parentNodeId == null ||
          String(node.parentNodeId).trim() === "" ||
          node.parentNodeId === "root" ||
          node.parentNodeId === "owner-node")
          ? "owner-node"
          : null),
      accessEnabled: Boolean(node.accessEnabled),
      isExistingUser: Boolean(node.isExistingUser),
      inviteStatus: node.inviteStatus || "none",
      inviteRequestedAt: node.inviteRequestedAt || null,
      inviteFirstSentAt: node.inviteFirstSentAt || node.inviteRequestedAt || null,
      inviteLastSentAt: node.inviteLastSentAt || node.inviteRequestedAt || null,
      inviteResentAt: node.inviteResentAt || null,
      inviteCancelledAt: node.inviteCancelledAt || null,
      inviteSendCount: Number(node.inviteSendCount) || 0,
      inviteResendAvailableAt: node.inviteResendAvailableAt || null,
      inviteResendReady: node.inviteResendReady !== false,
      inviteAcceptedAt: node.inviteAcceptedAt || null,
      invitationId: node.invitationId || null,
      createdAt: node.createdAt || null,
      updatedAt: node.updatedAt || node.createdAt || null,
      chartParentId: (() => {
        const p = node.parentNodeId;
        if (p == null || String(p).trim() === "" || p === "root") {
          return "owner-node";
        }
        return String(p);
      })(),
    }));
    setFamilyMembers([owner, ...mapped]);
  };

  const loadFamilyInvitations = async (token) => {
    const response = await fetch(`${API_BASE}/api/family-tree/invitations`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      throw new Error("Unable to load family invitations.");
    }
    const data = await response.json().catch(() => ({ items: [] }));
    const items = Array.isArray(data.items) ? data.items : [];
    setFamilyInvitations(
      items.map((item) => ({
        invitationId: item.invitationId,
        ownerUserId: item.ownerUserId || "",
        ownerNodeId: item.ownerNodeId || "",
        ownerName: item.ownerName || "Someone",
        ownerEmail: item.ownerEmail || "",
        inviteeEmail: item.inviteeEmail || "",
        inviteeName: item.inviteeName || "Family member",
        relation: item.relation || "Family member",
        inverseRelation: item.inverseRelation || item.relation || "Family member",
        status: item.status || "pending",
        sentAt: item.sentAt || item.createdAt || "",
        updatedAt: item.updatedAt || item.createdAt || "",
      }))
    );
    return items;
  };

  const completeSession = async (token, name, email) => {
    setSessionWarningOpen(false);
    setSessionExtendError("");
    const nextUser = { name, email };
    setAuthToken(token);
    setAuthUser(nextUser);
    persistSession(token, nextUser);
    if (selectedPlanId) {
      setView("plan");
      setAppPage("dashboard");
    } else {
      setView("app");
      setAppPage("dashboard");
    }
    setSelectedFeatureCard(null);
    setAppNotice("");
    setAuthForm({ name: "", email: "", password: "" });
    await loadFamilyTree(token, nextUser);
    try {
      const invitations = await loadFamilyInvitations(token);
      if (Array.isArray(invitations) && invitations.length) {
          setAppNotice(
            invitations.length === 1
              ? appCopy.familyInvitationNotice.waitingOne
              : appCopy.familyInvitationNotice.waitingMany(invitations.length)
          );
      }
    } catch {
      setFamilyInvitations([]);
    }
  };

  useEffect(() => {
    // Initialize positions for any new nodes while preserving dragged layout.
    setNodePositions((prev) => {
      const next = { ...prev };
      familyMembers.forEach((member, index) => {
        if (next[member.id]) {
          return;
        }
        const def = familyChartDefaultPositions[member.id];
        if (def) {
          next[member.id] = { x: def.x, y: def.y };
        } else {
          const x = 56 + (index % 3) * 246;
          const y = 48 + (index % 4) * 136;
          next[member.id] = { x, y };
        }
      });
      return next;
    });
  }, [familyMembers, familyChartDefaultPositions]);

  const getSvgPoint = (event) => {
    const svg = svgRef.current;
    if (!svg) {
      return { x: 0, y: 0 };
    }
    const pt = svg.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    const matrix = svg.getScreenCTM();
    if (!matrix) {
      return { x: 0, y: 0 };
    }
    const local = pt.matrixTransform(matrix.inverse());
    return { x: local.x, y: local.y };
  };

  const onNodePointerDown = (event, memberId) => {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    setSelectedNodeId(memberId);
    const start = getSvgPoint(event);
    const current =
      nodePositions[memberId] ?? familyChartDefaultPositions[memberId] ?? { x: 0, y: 0 };
    dragStateRef.current = {
      memberId,
      pointerId: event.pointerId,
      offsetX: start.x - current.x,
      offsetY: start.y - current.y,
    };
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // ignore
    }
  };

  const onNodePointerMove = (event) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    event.preventDefault();
    const p = getSvgPoint(event);
    const x = Math.max(0, Math.min(familyChartViewWidth - CHART_CARD_W, p.x - drag.offsetX));
    const y = Math.max(0, Math.min(familyChartViewHeight - CHART_CARD_H, p.y - drag.offsetY));
    setNodePositions((prev) => ({
      ...prev,
      [drag.memberId]: { x, y },
    }));
  };

  const onNodePointerUp = (event) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    event.preventDefault();
    dragStateRef.current = null;
  };

  const signOut = useCallback(() => {
    const currentToken = authTokenRef.current;
    if (currentToken) {
      fetch(`${API_BASE}/api/auth/session/end`, {
        method: "POST",
        headers: { Authorization: `Bearer ${currentToken}` },
      }).catch(() => {});
    }
    setAccountMenuOpen(false);
    setChangePasswordOpen(false);
    setChangePasswordMessage("");
    setChangePasswordFeedbackError(false);
    setSessionWarningOpen(false);
    setSessionExtendError("");
    setFamilyDeleteConfirm(null);
    setUpgradeReminderOpen(false);
    setUpgradeReminder(EMPTY_UPGRADE_REMINDER);
    setSignedInSessionStartedAt(0);
    clearStoredSession();
    setAuthToken("");
    setAuthUser(null);
    setFamilyMembers([]);
    setFamilyInvitations([]);
    setAuthError("");
    setSelectedFeatureCard(null);
    setAppNotice("");
    setAdminSession({
      checked: false,
      configured: false,
      canAccess: false,
      email: "",
    });
    setAppPage("dashboard");
    setView("home");
    stripShareablePageParams();
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!authToken) {
      setAdminSession({
        checked: false,
        configured: false,
        canAccess: false,
        email: "",
      });
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/session`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) {
          return;
        }
        if (!res.ok) {
          setAdminSession({
            checked: true,
            configured: false,
            canAccess: false,
            email: "",
          });
          return;
        }
        setAdminSession({
          checked: true,
          configured: Boolean(data.configured),
          canAccess: Boolean(data.canAccess),
          email: data.email || "",
        });
      } catch {
        if (!cancelled) {
          setAdminSession({
            checked: true,
            configured: false,
            canAccess: false,
            email: "",
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authToken]);

  useEffect(() => {
    if (view !== "app" || appPage !== "admin-ops" || !adminSession.checked) {
      return;
    }
    if (!adminSession.canAccess) {
      setAppPage("dashboard");
      setAppNotice("This account does not have owner operations access.");
    }
  }, [adminSession.canAccess, adminSession.checked, appPage, view]);

  const applyLocalUpgradeReminderEvent = useCallback((event, atIso) => {
    const iso = atIso || new Date().toISOString();
    setUpgradeReminder((prev) => {
      if (!prev.loaded) {
        return prev;
      }
      const intervalMs = Math.max(1, Number(prev.intervalMinutes) || 20) * 60 * 1000;
      if (event === "shown") {
        return {
          ...prev,
          lastShownAt: iso,
          shownCount: (Number(prev.shownCount) || 0) + 1,
          nextEligibleAt: new Date(Date.parse(iso) + intervalMs).toISOString(),
          canPromptFromHistory: false,
        };
      }
      if (event === "dismissed") {
        return {
          ...prev,
          lastDismissedAt: iso,
        };
      }
      if (event === "opened_pricing") {
        return {
          ...prev,
          lastOpenedPricingAt: iso,
        };
      }
      return prev;
    });
  }, []);

  const recordUpgradeReminderEvent = useCallback(
    async (event) => {
      if (!authToken) {
        return null;
      }
      const optimisticAt = new Date().toISOString();
      applyLocalUpgradeReminderEvent(event, optimisticAt);
      try {
        const res = await fetch(`${API_BASE}/api/upgrade-reminder`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ event }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.status === 401) {
          signOut();
          return null;
        }
        if (!res.ok) {
          throw new Error(data.message || "Could not record reminder activity.");
        }
        setUpgradeReminder({ loaded: true, ...data });
        return data;
      } catch {
        return null;
      }
    },
    [applyLocalUpgradeReminderEvent, authToken, signOut]
  );

  useEffect(() => {
    authTokenRef.current = authToken;
  }, [authToken]);

  useEffect(() => {
    let cancelled = false;
    if (!isSignedIn) {
      setUpgradeReminder(EMPTY_UPGRADE_REMINDER);
      setUpgradeReminderOpen(false);
      setSignedInSessionStartedAt(0);
      return () => {
        cancelled = true;
      };
    }

    setUpgradeReminderOpen(false);
    setSignedInSessionStartedAt((prev) => prev || Date.now());

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/upgrade-reminder`, {
          headers: { Authorization: `Bearer ${authTokenRef.current}` },
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) {
          return;
        }
        if (res.status === 401) {
          signOut();
          return;
        }
        if (!res.ok) {
          setUpgradeReminder({ ...EMPTY_UPGRADE_REMINDER, loaded: true });
          return;
        }
        setUpgradeReminder({ loaded: true, ...data });
      } catch {
        if (!cancelled) {
          setUpgradeReminder({ ...EMPTY_UPGRADE_REMINDER, loaded: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isSignedIn, signOut]);

  useEffect(() => {
    if (!authToken) {
      setSessionWarningOpen(false);
      return undefined;
    }
    const tick = () => {
      const expMs = parseJwtExpMs(authToken);
      if (expMs == null) {
        return;
      }
      const remaining = expMs - Date.now();
      if (remaining <= 0) {
        setSessionWarningOpen(false);
        signOut();
      } else if (remaining <= SESSION_WARN_WHEN_REMAINING_MS) {
        setSessionWarningOpen(true);
      } else {
        setSessionWarningOpen(false);
      }
    };
    tick();
    const id = window.setInterval(tick, SESSION_CHECK_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [authToken, signOut]);

  useEffect(() => {
    if (!sessionWarningOpen) {
      return undefined;
    }
    const id = window.setInterval(() => {
      setSessionClock((c) => c + 1);
    }, 1000);
    return () => window.clearInterval(id);
  }, [sessionWarningOpen]);

  const extendSession = useCallback(async () => {
    if (!authToken || !authUser) {
      return;
    }
    setSessionExtendBusy(true);
    setSessionExtendError("");
    try {
      const res = await fetch(`${API_BASE}/api/auth/session/extend`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401) {
          signOut();
          return;
        }
        throw new Error(data.message || "Could not extend your session.");
      }
      if (!data.token) {
        throw new Error("No token returned from server.");
      }
      setAuthToken(data.token);
      persistSession(data.token, authUser);
      setSessionWarningOpen(false);
    } catch (err) {
      setSessionExtendError(err.message || "Something went wrong.");
    } finally {
      setSessionExtendBusy(false);
    }
  }, [authToken, authUser, signOut]);

  const handleProfileSaved = useCallback(
    (profile) => {
      if (!profile || !authToken) {
        return;
      }
      setAuthUser((prev) => {
        if (!prev) {
          return prev;
        }
        const next = {
          ...prev,
          name: profile.name || prev.name || "You",
          email: profile.email || prev.email || "",
        };
        persistSession(authToken, next);
        return next;
      });
    },
    [authToken]
  );

  useEffect(() => {
    if (
      view === "plan" ||
      view === "terms" ||
      view === "security" ||
      view === "how" ||
      (view === "app" &&
        (appPage === "family" ||
          appPage === "media" ||
          appPage === "verification" ||
          appPage === "create-will" ||
          appPage === "profile" ||
          appPage === "payment-history"))
    ) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [view, appPage]);

  useEffect(() => {
    const onPopState = () => {
      setLocale(readStoredLocale());
      const requestedPublicView = readPublicViewFromSearch(window.location.search);
      if (requestedPublicView !== "home") {
        setView(requestedPublicView);
        return;
      }
      const stored = readStoredSession();
      if (stored?.token) {
        setView("app");
        setAppPage("dashboard");
      } else {
        setView("home");
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (!accountMenuOpen) {
      return undefined;
    }
    const onPointerDown = (event) => {
      const el = accountMenuRef.current;
      if (el && !el.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [accountMenuOpen]);

  const submitChangePassword = async (event) => {
    event.preventDefault();
    setChangePasswordMessage("");
    setChangePasswordFeedbackError(false);
    const { currentPassword, newPassword, confirm } = changePasswordForm;
    if (!currentPassword || !newPassword) {
      setChangePasswordFeedbackError(true);
      setChangePasswordMessage(appCopy.modals.changePassword.fillAllFields);
      return;
    }
    if (newPassword.length < 8) {
      setChangePasswordFeedbackError(true);
      setChangePasswordMessage(appCopy.modals.changePassword.minLength);
      return;
    }
    if (newPassword !== confirm) {
      setChangePasswordFeedbackError(true);
      setChangePasswordMessage(appCopy.modals.changePassword.mismatch);
      return;
    }
    setChangePasswordBusy(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setChangePasswordFeedbackError(true);
        setChangePasswordMessage(data.message || appCopy.modals.changePassword.updateFailed);
        return;
      }
      setChangePasswordFeedbackError(false);
      setChangePasswordForm({ currentPassword: "", newPassword: "", confirm: "" });
      setChangePasswordMessage(data.message || appCopy.modals.changePassword.updated);
      setTimeout(() => {
        setChangePasswordOpen(false);
        setChangePasswordMessage("");
      }, 2000);
    } finally {
      setChangePasswordBusy(false);
    }
  };

  useEffect(() => {
    if (view !== "app") {
      return undefined;
    }

    const nodes = sectionIds.map((id) => sectionRefs.current[id]).filter(Boolean);
    if (nodes.length === 0) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const best = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (best?.target?.id) {
          setActiveSection(best.target.id);
        }
      },
      { threshold: [0.25, 0.4, 0.6], rootMargin: "-20% 0px -70% 0px" }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [sectionIds, view]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const requestedPublicView = readPublicViewFromSearch(window.location.search);
      const reset = params.get("reset");
      if (reset) {
        const url = new URL(window.location.href);
        url.searchParams.delete("reset");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
        if (cancelled) {
          return;
        }
        setAuthError("");
        setAuthSuccessBanner("");
        setResetToken(reset);
        setResetPasswordForm({ password: "", confirm: "" });
        setView("auth");
        setAuthMode("reset");
        return;
      }

      const magic = params.get("magic");
      if (magic) {
        const url = new URL(window.location.href);
        url.searchParams.delete("magic");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
        setAuthError("");
        const response = await fetch(`${API_BASE}/api/auth/magic-link/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: magic }),
        });
        const data = await response.json().catch(() => ({}));
        if (cancelled) {
          return;
        }
        if (!response.ok) {
          setAuthError(data.message || "Sign-in link is invalid or expired.");
          setView("auth");
          setAuthMode("login");
          return;
        }
        await completeSession(data.token, data.name, data.email);
        if (cancelled) {
          return;
        }
        if (requestedPublicView !== "home") {
          setView(requestedPublicView);
        }
        return;
      }

      const stored = readStoredSession();
      if (!stored?.token) {
        if (!cancelled && requestedPublicView !== "home") {
          setView(requestedPublicView);
        }
        return;
      }
      const profileRes = await fetch(`${API_BASE}/api/profile`, {
        headers: { Authorization: `Bearer ${stored.token}` },
      });
      if (cancelled) {
        return;
      }
      if (!profileRes.ok) {
        clearStoredSession();
        return;
      }
      const profile = await profileRes.json();
      const merged = {
        name: profile?.name || stored.user?.name || "You",
        email: profile?.email || stored.user?.email || "",
      };
      setAuthToken(stored.token);
      setAuthUser(merged);
      persistSession(stored.token, merged);
      setView("app");
      setAppPage("dashboard");
      await loadFamilyTree(stored.token, merged);
      let invitations = [];
      try {
        invitations = await loadFamilyInvitations(stored.token);
      } catch {
        setFamilyInvitations([]);
      }
      if (cancelled) {
        return;
      }
      if (Array.isArray(invitations) && invitations.length) {
          setAppNotice(
            invitations.length === 1
              ? appCopy.familyInvitationNotice.waitingOne
              : appCopy.familyInvitationNotice.waitingMany(invitations.length)
          );
      }
      if (requestedPublicView !== "home") {
        setView(requestedPublicView);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const submitAuth = async (event) => {
    event.preventDefault();
    setAuthError("");
    const endpoint = authMode === "login" ? "login" : "register";
    const payload =
      authMode === "login"
        ? { email: authForm.email, password: authForm.password }
        : authForm;

    const response = await fetch(`${API_BASE}/api/auth/${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await response.json();

    if (!response.ok) {
      setAuthError(data.message || (locale === "fr" ? "Échec de l'authentification" : "Authentication failed"));
      return;
    }

    await completeSession(data.token, data.name, data.email);
  };

  const handleGoogleCredential = async (credential) => {
    setAuthError("");
    setGoogleBusy(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setAuthError(data.message || "Google sign-in failed");
        return;
      }
      await completeSession(data.token, data.name, data.email);
    } finally {
      setGoogleBusy(false);
    }
  };

  const requestForgotPassword = async (event) => {
    event?.preventDefault?.();
    setAuthError("");
    setForgotHint("");
    setForgotDevUrl("");
    const email = authForm.email.trim();
    if (!email) {
      setAuthError("Enter the email for your account.");
      return;
    }
    setForgotBusy(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setAuthError(data.message || "Could not send reset instructions.");
        return;
      }
      setForgotHint(data.message || "Check your inbox.");
      if (data.devResetUrl) {
        setForgotDevUrl(data.devResetUrl);
      }
    } finally {
      setForgotBusy(false);
    }
  };

  const submitResetPassword = async (event) => {
    event.preventDefault();
    setAuthError("");
    if (!resetToken) {
      setAuthError("Reset link is missing. Request a new link from the sign-in page.");
      return;
    }
    const { password, confirm } = resetPasswordForm;
    if (password.length < 8) {
      setAuthError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setAuthError("Passwords do not match.");
      return;
    }
    setResetBusy(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetToken, password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setAuthError(data.message || "Could not reset password.");
        return;
      }
      setResetToken("");
      setResetPasswordForm({ password: "", confirm: "" });
      setAuthMode("login");
      setAuthSuccessBanner(data.message || "Password updated. You can sign in now.");
    } finally {
      setResetBusy(false);
    }
  };

  const requestMagicLink = async () => {
    setAuthError("");
    setMagicLinkHint("");
    setMagicLinkDevUrl("");
    const email = authForm.email.trim();
    if (!email) {
      setAuthError("Enter your email address first, then request a sign-in link.");
      return;
    }
    setMagicLinkBusy(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/magic-link/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setAuthError(data.message || "Could not send a sign-in link.");
        return;
      }
      setMagicLinkHint(data.message || "Check your inbox.");
      if (data.devMagicLink) {
        setMagicLinkDevUrl(data.devMagicLink);
      }
    } finally {
      setMagicLinkBusy(false);
    }
  };

  const loadMediaStats = useCallback(async () => {
    if (!authToken) return;
    try {
      const res = await fetch(`${API_BASE}/api/media/stats`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMediaStats((prev) => ({
          ...prev,
          loaded: true,
          notice: data.message || "Could not load media usage.",
        }));
        return;
      }
      setMediaStats({
        totalBytes: Number(data.totalBytes) || 0,
        totalCount: Number(data.totalCount) || 0,
        quotaBytes: Number(data.quotaBytes) || STORAGE_GB_LIMIT * 1024 * 1024 * 1024,
        mediaAvailable: data.mediaAvailable !== false,
        notice: typeof data.notice === "string" ? data.notice : "",
        loaded: true,
      });
    } catch {
      setMediaStats((prev) => ({
        ...prev,
        loaded: true,
        notice: "Could not load media usage.",
      }));
    }
  }, [authToken]);

  useEffect(() => {
    if (view === "app" && appPage === "media" && authToken) {
      loadMediaStats();
    }
  }, [view, appPage, authToken, loadMediaStats]);

  const addFamilyMember = async (event) => {
    event.preventDefault();
    setAppNotice("");
    setFamilyPlanLimitPrompt(null);
    if (!memberName.trim()) {
      setAppNotice("Enter a name for this family member.");
      window.setTimeout(() => setAppNotice(""), 5000);
      return;
    }
    if (!memberEmail.trim()) {
      setAppNotice("Enter an email so this person can receive and accept the family invitation.");
      window.setTimeout(() => setAppNotice(""), 5000);
      return;
    }
    if (memberRelation === "Grandchild" && !memberChartParentId) {
      setAppNotice("Choose which child this grandchild should appear under.");
      window.setTimeout(() => setAppNotice(""), 5000);
      return;
    }
    if (
      memberRelation === "Grandchild" &&
      !childBranchOptions.some((member) => member.backendNodeId === memberChartParentId)
    ) {
      setAppNotice("Grandchildren can only branch under a child in your chart.");
      window.setTimeout(() => setAppNotice(""), 5000);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/family-tree/nodes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          name: memberName.trim(),
          email: memberEmail.trim(),
          relation: memberRelation,
          nickname: memberNickname.trim(),
          pronouns: memberPronouns,
          identityNote: memberIdentityNote.trim(),
          specialNote: memberSpecialNote.trim(),
          parentNodeId: memberRelation === "Grandchild" ? memberChartParentId : "owner-node",
          ...((() => {
            const yNow = new Date().getFullYear();
            const n = Number(memberBirthYear);
            return memberBirthYear.trim() !== "" && Number.isFinite(n) && n >= 1900 && n <= yNow
              ? { birthYear: Math.floor(n) }
              : {};
          })()),
          ...(memberRelation === "Child" && memberCoParentId
            ? { coParentNodeId: memberCoParentId }
            : {}),
          ...((memberRelation === "Spouse" || memberRelation === "Partner") &&
          memberPartnerId
            ? { partnerNodeId: memberPartnerId }
            : {}),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(data.message || "Unable to create family member.");
        error.code = data.code || "";
        throw error;
      }
      setMemberName("");
      setMemberEmail("");
      setMemberNickname("");
      setMemberPronouns("");
      setMemberBirthYear("");
      setMemberCoParentId("");
      setMemberPartnerId("owner-node");
      setMemberChartParentId("");
      setMemberIdentityNote("");
      setMemberSpecialNote("");
      setFamilyPlanLimitPrompt(null);
      await loadFamilyTree(authToken);
    } catch (error) {
      if (error?.code === "PLAN_RELATION_LIMIT") {
        setFamilyPlanLimitPrompt({
          code: error.code,
          message: error.message || familyPlanLimitCopy.body,
        });
        return;
      }
      setAppNotice(error.message || "Unable to create family member.");
      window.setTimeout(() => setAppNotice(""), 6000);
    }
  };

  const askToJoin = async (member) => {
    if (!member?.backendNodeId || !authToken) {
      return;
    }
    setFamilyInviteBusyId(member.backendNodeId);
    try {
      const response = await fetch(
        `${API_BASE}/api/family-tree/nodes/${member.backendNodeId}/ask-to-join`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || "Could not send ask to join.");
      }
      setAppNotice(data.message || "Request completed.");
      window.setTimeout(() => setAppNotice(""), 8000);
      await loadFamilyTree(authToken);
      await loadFamilyInvitations(authToken);
    } catch (error) {
      setAppNotice(error.message || "Could not send invite.");
      window.setTimeout(() => setAppNotice(""), 6000);
    } finally {
      setFamilyInviteBusyId("");
    }
  };

  const resendFamilyInvite = async (member) => {
    if (!member?.backendNodeId || !authToken) {
      return;
    }
    setFamilyInviteBusyId(member.backendNodeId);
    try {
      const response = await fetch(
        `${API_BASE}/api/family-tree/nodes/${member.backendNodeId}/resend-invite`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || "Could not re-send invite.");
      }
      setAppNotice(data.message || "Invite re-sent.");
      window.setTimeout(() => setAppNotice(""), 8000);
      await loadFamilyTree(authToken);
      await loadFamilyInvitations(authToken);
    } catch (error) {
      setAppNotice(error.message || "Could not re-send invite.");
      window.setTimeout(() => setAppNotice(""), 6000);
    } finally {
      setFamilyInviteBusyId("");
    }
  };

  const cancelFamilyInvite = async (member) => {
    if (!member?.backendNodeId || !authToken) {
      return;
    }
    setFamilyInviteBusyId(member.backendNodeId);
    try {
      const response = await fetch(
        `${API_BASE}/api/family-tree/nodes/${member.backendNodeId}/cancel-invite`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || "Could not cancel invite.");
      }
      setAppNotice(data.message || "Invitation cancelled.");
      window.setTimeout(() => setAppNotice(""), 8000);
      await loadFamilyTree(authToken);
      await loadFamilyInvitations(authToken);
    } catch (error) {
      setAppNotice(error.message || "Could not cancel invite.");
      window.setTimeout(() => setAppNotice(""), 6000);
    }
    finally {
      setFamilyInviteBusyId("");
    }
  };

  const beginFamilyEmailEdit = (member) => {
    if (!member?.backendNodeId || member.relation === "Owner") {
      return;
    }
    setEditingFamilyEmailNodeId(member.backendNodeId);
    setEditingFamilyEmailValue(member.email || "");
  };

  const cancelFamilyEmailEdit = () => {
    if (familyEmailSavingId) {
      return;
    }
    setEditingFamilyEmailNodeId("");
    setEditingFamilyEmailValue("");
  };

  const saveFamilyMemberEmail = async (member) => {
    if (!member?.backendNodeId || !authToken) {
      return;
    }
    const nextEmail = editingFamilyEmailValue.trim();
    if (!nextEmail) {
      setAppNotice("Email is required for family members.");
      window.setTimeout(() => setAppNotice(""), 5000);
      return;
    }
    setFamilyEmailSavingId(member.backendNodeId);
    try {
      const response = await fetch(
        `${API_BASE}/api/family-tree/nodes/${member.backendNodeId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ email: nextEmail }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || "Could not update this email.");
      }
      await loadFamilyTree(authToken);
      await loadFamilyInvitations(authToken);
      setEditingFamilyEmailNodeId("");
      setEditingFamilyEmailValue("");
      setAppNotice(`${member.name}'s email was updated.`);
      window.setTimeout(() => setAppNotice(""), 5000);
    } catch (error) {
      setAppNotice(error.message || "Could not update this email.");
      window.setTimeout(() => setAppNotice(""), 6000);
    } finally {
      setFamilyEmailSavingId("");
    }
  };

  const getFamilyChartLayout = (member, index) => {
    const pos = nodePositions[member.id] ?? familyChartDefaultPositions[member.id];
    const x = pos?.x ?? 56 + (index % 3) * 246;
    const y = pos?.y ?? 48 + (index % 4) * 136;
    return {
      x,
      y,
      w: CHART_CARD_W,
      h: CHART_CARD_H,
      cx: x + CHART_CARD_W / 2,
      top: y,
      bottom: y + CHART_CARD_H,
    };
  };

  const requestDeleteFamilyMember = (member) => {
    if (
      !member?.backendNodeId ||
      member.relation === "Owner" ||
      member.backendNodeId === "owner-node"
    ) {
      return;
    }
    setFamilyDeleteConfirm({
      nodeId: member.backendNodeId,
      name: member.name || "Member",
    });
  };

  const cancelDeleteFamilyMember = () => {
    if (!familyDeleting) {
      setFamilyDeleteConfirm(null);
    }
  };

  const confirmDeleteFamilyMember = async () => {
    if (!authToken || !familyDeleteConfirm) {
      return;
    }
    setFamilyDeleting(true);
    const removedName = familyDeleteConfirm.name;
    const removedNodeId = familyDeleteConfirm.nodeId;
    const removedListId = `node-${removedNodeId}`;
    try {
      const res = await fetch(
        `${API_BASE}/api/family-tree/nodes/${encodeURIComponent(removedNodeId)}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok && res.status !== 204) {
        throw new Error(data.message || "Could not remove this member.");
      }
      setFamilyDeleteConfirm(null);
      await loadFamilyTree(authToken);
      await loadFamilyInvitations(authToken);
      setSelectedRecipient((prev) =>
        prev === removedNodeId || prev === removedListId ? "all-family" : prev
      );
      setMediaGalleryTick((n) => n + 1);
      setAppNotice(`${removedName} was removed from your chart.`);
      window.setTimeout(() => setAppNotice(""), 5000);
    } catch (e) {
      setAppNotice(e.message || "Could not remove this member.");
      window.setTimeout(() => setAppNotice(""), 6000);
    } finally {
      setFamilyDeleting(false);
    }
  };

  const acceptFamilyInvitation = async (invitation) => {
    if (!invitation?.invitationId || !authToken) {
      return;
    }
    setFamilyInvitationBusyId(invitation.invitationId);
    try {
      const response = await fetch(
        `${API_BASE}/api/family-tree/invitations/${encodeURIComponent(invitation.invitationId)}/accept`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || "Could not accept this invitation.");
      }
      await loadFamilyTree(authToken, authUser);
      await loadFamilyInvitations(authToken);
      setAppNotice(appCopy.familyInvitationNotice.connectedWith(invitation.ownerName));
      window.setTimeout(() => setAppNotice(""), 8000);
    } catch (error) {
      setAppNotice(error.message || "Could not accept this invitation.");
      window.setTimeout(() => setAppNotice(""), 6000);
    } finally {
      setFamilyInvitationBusyId("");
    }
  };

  const publicViewSet = useMemo(
    () => new Set(["home", "plan", "terms", "security", "how"]),
    []
  );
  const isPublicView = publicViewSet.has(view);
  const showGuestContact = isPublicView && !authToken && view !== "auth";
  const publicCopy = useMemo(() => getPublicSiteCopy(locale), [locale]);
  const navCopy = publicCopy.nav;
  const homeCopy = publicCopy.home;
  const authCopy = publicCopy.auth;
  const pricingCopy = publicCopy.pricingPage;
  const termsCopy = publicCopy.termsPage;
  const upgradeReminderCopy = publicCopy.upgradeReminder;
  const homeHeroCopy = publicCopy.homeHero;
  const appCopy = useMemo(() => getAppCopy(locale), [locale]);
  const showHomeVideoSection = Boolean(HOME_VIDEO_URL) || process.env.NODE_ENV !== "production";
  const showHomeVideoDevPlaceholder =
    !HOME_VIDEO_URL && process.env.NODE_ENV !== "production";
  const appFamilyCopy = APP_FAMILY_COPY[locale === "fr" ? "fr" : "en"];
  const featureCards = useMemo(
    () => buildFeatureCards(appCopy, appFamilyCopy.createWillCard),
    [appCopy, appFamilyCopy]
  );
  const verificationOptions = useMemo(() => appCopy.verificationOptions, [appCopy]);
  const selectedVerification = verificationOptions.find(
    (option) => option.id === verificationId
  );
  const familyPlanLimitCopy =
    locale === "fr"
      ? {
          title: "Les amis et autres contacts de confiance sont debloques avec l'essai",
          body:
            "Le plan gratuit prend actuellement en charge le conjoint et les enfants seulement. Demarrez l'essai pour ajouter un ami, un conseiller ou d'autres personnes de confiance a votre plan.",
          primaryCta: "Demarrer l'essai",
          secondaryCta: "Voir les forfaits",
          successNotice:
            "Votre essai est actif. Vous pouvez maintenant ajouter des amis et d'autres personnes de confiance.",
        }
      : {
          title: "Friends and other trusted contacts unlock during trial",
          body:
            "The free plan currently supports spouse and child members only. Start the trial to add a friend, advisor, or other trusted person to your plan.",
          primaryCta: "Start trial",
          secondaryCta: "See plans",
          successNotice:
            "Your trial is active. You can now add friends and other trusted people.",
        };
  const selectedPlan =
    pricingCopy.tiles.find((tile) => tile.id === selectedPlanId) || null;
  const selectedPlanSubscription =
    planEnrollmentResult?.planId && planEnrollmentResult.planId === selectedPlanId
      ? planEnrollmentResult
      : null;
  const selectedPlanPreviewStart = useMemo(
    () => (selectedPlan ? new Date().toISOString() : ""),
    [selectedPlan]
  );
  const selectedPlanPreviewExpiry = useMemo(
    () => addMonthsPreview(selectedPlanPreviewStart, PLAN_INTRO_TRIAL_MONTHS),
    [selectedPlanPreviewStart]
  );

  useEffect(() => {
    if (view !== "plan") {
      return undefined;
    }
    const validIds = new Set(pricingCopy.tiles.map((t) => t.id));
    if (selectedPlanId && !validIds.has(selectedPlanId)) {
      setSelectedPlanId("");
      persistSelectedPlan("");
    }
    return undefined;
  }, [view, selectedPlanId, pricingCopy.tiles]);

  const changeLocale = useCallback((nextLocale) => {
    const normalized = nextLocale === "fr" ? "fr" : "en";
    setLocale(normalized);
    writeStoredLocale(normalized);
    writeLocaleToUrl(normalized, "replace");
  }, []);

  const openPublicView = (nextView) => {
    setAppNotice("");
    setView(nextView);
    writePublicViewToUrl(nextView);
  };

  const returnToDashboard = useCallback(() => {
    setAppPage("dashboard");
    setActiveSection("overview");
    scrollToSection("overview");
  }, [scrollToSection]);

  const openCreateWillPage = useCallback(() => {
    setSelectedFeatureCard(null);
    setAppPage("create-will");
    setActiveSection("create-will");
  }, []);

  const guideProfileComplete = useMemo(() => {
    if (!authUser) {
      return false;
    }
    return Boolean(String(authUser.name || "").trim() && String(authUser.email || "").trim());
  }, [authUser]);

  const guideTrustedContactCount = useMemo(
    () => familyMembers.filter((member) => member.relation !== "Owner").length,
    [familyMembers]
  );

  const openHomeSection = (sectionId) => {
    setAppNotice("");
    if (view !== "home") {
      setView("home");
      writePublicViewToUrl("home");
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const node = sectionRefs.current[sectionId] || document.getElementById(sectionId);
          if (node) {
            node.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      });
      return;
    }
    const node = sectionRefs.current[sectionId] || document.getElementById(sectionId);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const openHowPage = () => openPublicView("how");
  const openSecurityPage = () => openPublicView("security");
  const openPlanPage = () => {
    setPlanEntrySource("");
    openPublicView("plan");
  };

  const selectPricingPlan = (planId) => {
    setSelectedPlanId(planId);
    persistSelectedPlan(planId);
    setPlanEnrollmentError("");
    setPlanEnrollmentResult(null);
    setPlanEntrySource("");
    openPlanPage();
  };

  const startTrialFromFamilyLimit = useCallback(() => {
    const defaultPlanId = "monthly";
    setSelectedPlanId(defaultPlanId);
    persistSelectedPlan(defaultPlanId);
    setPlanEnrollmentError("");
    setPlanEnrollmentResult(null);
    setPlanEntrySource("family-plan-limit");
    openPublicView("plan");
  }, []);

  const openTermsPage = () => {
    openPublicView("terms");
  };

  const dismissUpgradeReminder = () => {
    setUpgradeReminderOpen(false);
    recordUpgradeReminderEvent("dismissed");
  };

  const openPlanPageFromReminder = () => {
    setUpgradeReminderOpen(false);
    recordUpgradeReminderEvent("opened_pricing");
    openPlanPage();
  };

  const openSignIn = () => {
    stripShareablePageParams();
    setAuthMode("login");
    setView("auth");
  };

  const openGetStarted = () => {
    stripShareablePageParams();
    setAuthMode("register");
    setView("auth");
  };

  const enrollSelectedPlan = async () => {
    if (!authToken || !selectedPlan) {
      return;
    }
    setPlanEnrollmentBusy(true);
    setPlanEnrollmentError("");
    try {
      const response = await fetch(`${API_BASE}/api/subscription/enroll`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ planId: selectedPlan.id }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || "Could not enroll this plan right now.");
      }
      persistSelectedPlan("");
      setPlanEnrollmentResult(data.subscription || null);
      if (planEntrySource === "family-plan-limit") {
        setFamilyPlanLimitPrompt(null);
        setView("app");
        setAppPage("family");
        setAppNotice(
          data.emailSent
            ? `${familyPlanLimitCopy.successNotice} Confirmation email sent.`
            : familyPlanLimitCopy.successNotice
        );
        setSelectedPlanId("");
        setPlanEnrollmentResult(null);
        setPlanEntrySource("");
      } else {
        setAppNotice(
          data.emailSent
            ? "Trial started and confirmation email sent."
            : "Trial started on your account."
        );
      }
      window.setTimeout(() => setAppNotice(""), 5000);
    } catch (error) {
      setPlanEnrollmentError(error.message || "Could not enroll this plan right now.");
    } finally {
      setPlanEnrollmentBusy(false);
    }
  };

  const handleBrandHome = () => {
    if (authToken) {
      setAppPage("dashboard");
      setView("app");
    } else {
      writePublicViewToUrl("home");
      setView("home");
    }
  };

  useEffect(() => {
    if (!upgradeReminderOpen) {
      return;
    }
    if (
      view !== "app" ||
      appPage === "admin-ops" ||
      sessionWarningOpen ||
      changePasswordOpen ||
      familyDeleteConfirm
    ) {
      setUpgradeReminderOpen(false);
    }
  }, [
    appPage,
    changePasswordOpen,
    familyDeleteConfirm,
    sessionWarningOpen,
    upgradeReminderOpen,
    view,
  ]);

  useEffect(() => {
    if (
      !authToken ||
      !signedInSessionStartedAt ||
      !upgradeReminder.loaded ||
      !upgradeReminder.isFreePlan ||
      view !== "app" ||
      appPage === "admin-ops" ||
      sessionWarningOpen ||
      changePasswordOpen ||
      familyDeleteConfirm ||
      upgradeReminderOpen
    ) {
      return undefined;
    }

    const intervalMs =
      Math.max(1, Number(upgradeReminder.intervalMinutes) || 20) * 60 * 1000;
    const sessionEligibleAt = signedInSessionStartedAt + intervalMs;
    const historyEligibleAt = upgradeReminder.nextEligibleAt
      ? Date.parse(upgradeReminder.nextEligibleAt)
      : null;
    const targetMs =
      historyEligibleAt != null
        ? Math.max(sessionEligibleAt, historyEligibleAt)
        : sessionEligibleAt;
    const delayMs = Math.max(0, targetMs - Date.now());

    const id = window.setTimeout(() => {
      setUpgradeReminderOpen(true);
      recordUpgradeReminderEvent("shown");
    }, delayMs);

    return () => window.clearTimeout(id);
  }, [
    appPage,
    authToken,
    changePasswordOpen,
    familyDeleteConfirm,
    recordUpgradeReminderEvent,
    sessionWarningOpen,
    signedInSessionStartedAt,
    upgradeReminder.intervalMinutes,
    upgradeReminder.isFreePlan,
    upgradeReminder.loaded,
    upgradeReminder.nextEligibleAt,
    upgradeReminderOpen,
    view,
  ]);

  useEffect(() => {
    writeStoredLocale(locale);
    writeLocaleToUrl(locale, "replace");
  }, [locale]);

  useEffect(() => {
    const meta =
      view === "auth"
        ? getPageMetadata(locale, "auth")
        : getPageMetadata(locale, view);
    document.title = meta.title;
    document.documentElement.lang = publicCopy.htmlLang;
    const upsert = (selector, attrName, attrValue, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    upsert('meta[name="description"]', "name", "description", meta.description);
    upsert('meta[property="og:title"]', "property", "og:title", meta.title);
    upsert(
      'meta[property="og:description"]',
      "property",
      "og:description",
      meta.description
    );
  }, [locale, publicCopy.htmlLang, view]);

  const copyPlanPageLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setAppNotice(publicCopy.notices.planLinkCopied);
    } catch {
      setAppNotice(publicCopy.notices.linkCopyFailed);
    }
    window.setTimeout(() => setAppNotice(""), 6000);
  };

  const copyTermsPageLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setAppNotice(publicCopy.notices.termsLinkCopied);
    } catch {
      setAppNotice(publicCopy.notices.linkCopyFailed);
    }
    window.setTimeout(() => setAppNotice(""), 6000);
  };

  let sessionSecondsLeft = 0;
  if (sessionWarningOpen && authToken) {
    const expMs = parseJwtExpMs(authToken);
    if (expMs != null) {
      sessionSecondsLeft = Math.max(0, Math.floor((expMs - Date.now()) / 1000));
    }
  }

  return (
    <>
    <main className="site">
      <header className={view === "security" || view === "how" ? "hero hero-compact" : "hero"}>
        <nav className="top-nav">
          {isPublicView ? (
            <>
              <div className="top-nav-lead">
                <button type="button" className="brand brand-button" onClick={handleBrandHome}>
                  AfterMe.ca
                </button>
                <button
                  type="button"
                  className={`nav-link${view === "home" ? " nav-link-current" : ""}`}
                  onClick={() => openHomeSection("product")}
                >
                  {navCopy.product}
                </button>
                <button
                  type="button"
                  className={`nav-link${view === "how" ? " nav-link-current" : ""}`}
                  onClick={openHowPage}
                >
                  {navCopy.how}
                </button>
                <button
                  type="button"
                  className={`nav-link${view === "security" ? " nav-link-current" : ""}`}
                  onClick={openSecurityPage}
                >
                  {navCopy.security}
                </button>
                <button
                  type="button"
                  className={`nav-link${view === "plan" ? " nav-link-current" : ""}`}
                  onClick={openPlanPage}
                >
                  {navCopy.pricing}
                </button>
                <button type="button" className="nav-link" onClick={() => openHomeSection("faq")}>
                  {navCopy.faq}
                </button>
              </div>
              <div className="top-nav-guest-actions">
                <div className="nav-locale-toggle" aria-label={publicCopy.toggle.label}>
                  <div className="nav-locale-buttons">
                    <button
                      type="button"
                      className={`nav-locale-button${locale === "en" ? " active" : ""}`}
                      onClick={() => changeLocale("en")}
                      aria-pressed={locale === "en"}
                    >
                      {publicCopy.toggle.english}
                    </button>
                    <button
                      type="button"
                      className={`nav-locale-button${locale === "fr" ? " active" : ""}`}
                      onClick={() => changeLocale("fr")}
                      aria-pressed={locale === "fr"}
                    >
                      {publicCopy.toggle.french}
                    </button>
                  </div>
                </div>
                {authToken ? (
                  <>
                    <button
                      type="button"
                      className="nav-link"
                      onClick={() =>
                        loadFamilyTree(authToken, authUser).then(() => {
                          setAppPage("dashboard");
                          setView("app");
                        })
                      }
                    >
                      {navCopy.myDashboard}
                    </button>
                    <button type="button" className="nav-cta" onClick={signOut}>
                      {navCopy.signOut}
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="nav-link" onClick={openSignIn}>
                      {navCopy.signIn}
                    </button>
                    <button type="button" className="nav-cta" onClick={openGetStarted}>
                      {navCopy.getStarted}
                    </button>
                  </>
                )}
              </div>
            </>
          ) : null}
          {view === "app" && (
            <>
              <div className="top-nav-app-actions">
                <div className="top-nav-app-tabs">
                  <button
                    type="button"
                    className={`nav-tab ${appPage === "dashboard" && activeSection === "overview" ? "active" : ""}`}
                    onClick={() => {
                      setAppPage("dashboard");
                      scrollToSection("overview");
                    }}
                  >
                    {appCopy.nav.overview}
                  </button>
                  <button
                    type="button"
                    className={`nav-tab ${appPage === "dashboard" && activeSection === "journal" && selectedFeatureCard !== "photo-memory-collage" ? "active" : ""}`}
                    onClick={() => {
                      setAppPage("dashboard");
                      setSelectedFeatureCard("private-journals");
                      setRequestedJournalId("");
                      scrollToSection("journal");
                    }}
                  >
                    {appCopy.nav.journal}
                  </button>
                  <button
                    type="button"
                    className={`nav-tab ${appPage === "dashboard" && activeSection === "features" ? "active" : ""}`}
                    onClick={() => {
                      setAppPage("dashboard");
                      scrollToSection("features");
                    }}
                  >
                    {appCopy.nav.features}
                  </button>
                  <button
                    type="button"
                    className={`nav-tab ${appPage === "family" ? "active" : ""}`}
                    onClick={() => {
                      setAppPage("family");
                      setActiveSection("family");
                    }}
                  >
                    {appCopy.nav.familyChart}
                  </button>
                  <button
                    type="button"
                    className={`nav-tab ${appPage === "media" ? "active" : ""}`}
                    onClick={() => {
                      setAppPage("media");
                      setActiveSection("media");
                    }}
                  >
                    {appCopy.nav.media}
                  </button>
                  <button
                    type="button"
                    className={`nav-tab ${appPage === "verification" ? "active" : ""}`}
                    onClick={() => {
                      setAppPage("verification");
                      setActiveSection("verification");
                    }}
                  >
                    {appCopy.nav.verification}
                  </button>
                  <button
                    type="button"
                    className={`nav-tab ${appPage === "create-will" ? "active" : ""}`}
                    onClick={openCreateWillPage}
                  >
                    {appFamilyCopy.createWillCard.title}
                  </button>
                </div>
                <button
                  type="button"
                  className={`nav-gallery-cta ${photoGalleryNavActive ? "active" : ""}`}
                  onClick={openPhotoMemoryGallery}
                >
                  {appCopy.nav.photoGallery}
                </button>
                <div className="account-menu-wrap" ref={accountMenuRef}>
                <button
                  type="button"
                  className="account-menu-trigger"
                  aria-expanded={accountMenuOpen}
                  aria-haspopup="true"
                  onClick={() => setAccountMenuOpen((open) => !open)}
                >
                  {appCopy.nav.myAccount}
                  <span className="account-menu-chevron" aria-hidden>
                    ▾
                  </span>
                </button>
                {accountMenuOpen ? (
                  <div className="account-menu-dropdown" role="menu">
                    {adminSession.canAccess ? (
                      <button
                        type="button"
                        className="account-menu-item"
                        role="menuitem"
                        onClick={() => {
                          setAccountMenuOpen(false);
                          stripShareablePageParams();
                          setAppPage("admin-ops");
                        }}
                      >
                        {appCopy.accountMenu.operationsConsole}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="account-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setAccountMenuOpen(false);
                        stripShareablePageParams();
                        setAppPage("profile");
                      }}
                      >
                      {appCopy.accountMenu.myProfile}
                    </button>
                    <button
                      type="button"
                      className="account-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setAccountMenuOpen(false);
                        stripShareablePageParams();
                        setAppPage("payment-history");
                      }}
                      >
                      {appCopy.accountMenu.paymentHistory}
                    </button>
                    <button
                      type="button"
                      className="account-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setAccountMenuOpen(false);
                        setChangePasswordForm({
                          currentPassword: "",
                          newPassword: "",
                          confirm: "",
                        });
                        setChangePasswordMessage("");
                        setChangePasswordFeedbackError(false);
                        setChangePasswordOpen(true);
                      }}
                      >
                      {appCopy.accountMenu.changePassword}
                    </button>
                    <button
                      type="button"
                      className="account-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setAccountMenuOpen(false);
                        signOut();
                      }}
                      >
                      {appCopy.accountMenu.signOut}
                    </button>
                  </div>
                ) : null}
                </div>
              </div>
            </>
          )}
          {view === "auth" && (
            <>
              <div className="top-nav-lead">
                <button type="button" className="brand brand-button" onClick={handleBrandHome}>
                  AfterMe.ca
                </button>
              </div>
              <div className="top-nav-guest-actions">
                <div className="nav-locale-toggle" aria-label={publicCopy.toggle.label}>
                  <div className="nav-locale-buttons">
                    <button
                      type="button"
                      className={`nav-locale-button${locale === "en" ? " active" : ""}`}
                      onClick={() => changeLocale("en")}
                      aria-pressed={locale === "en"}
                    >
                      {publicCopy.toggle.english}
                    </button>
                    <button
                      type="button"
                      className={`nav-locale-button${locale === "fr" ? " active" : ""}`}
                      onClick={() => changeLocale("fr")}
                      aria-pressed={locale === "fr"}
                    >
                      {publicCopy.toggle.french}
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  className="nav-link"
                  onClick={() => {
                    stripShareablePageParams();
                    setView("home");
                  }}
                >
                  {navCopy.backToHome}
                </button>
              </div>
            </>
          )}
        </nav>
        {view === "security" || view === "how" ? null : (
          <div className="hero-content">
            {view === "terms" ? (
            <>
              <p className="eyebrow">{termsCopy.heroEyebrow}</p>
              <h1>{termsCopy.heroTitle}</h1>
              <p>{termsCopy.heroBody}</p>
              <div className="hero-actions">
                <button type="button" onClick={copyTermsPageLink}>
                  {termsCopy.copyLink}
                </button>
                {authToken ? (
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => {
                      stripShareablePageParams();
                      setAppPage("dashboard");
                      setView("app");
                    }}
                  >
                    {termsCopy.backToDashboard}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => {
                      setAuthMode("login");
                      setView("auth");
                    }}
                  >
                    {termsCopy.signIn}
                  </button>
                )}
              </div>
            </>
            ) : view === "plan" ? (
            <div className="hero-content-inner hero-content-with-plan-page">
              <div className="plan-hero-copy">
                <p className="eyebrow">{pricingCopy.heroEyebrow}</p>
                <h1>{pricingCopy.heroTitle}</h1>
                <p>{pricingCopy.heroBody}</p>
                <div className="hero-actions">
                  <button
                    type="button"
                    onClick={() => {
                      if (authToken) {
                        document
                          .getElementById("plan-heading")
                          ?.scrollIntoView({ behavior: "smooth", block: "start" });
                        return;
                      }
                      openGetStarted();
                    }}
                  >
                    {pricingCopy.heroPrimaryCta}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() =>
                      document
                        .getElementById("plan-value-section")
                        ?.scrollIntoView({ behavior: "smooth", block: "start" })
                    }
                  >
                    {pricingCopy.heroSecondaryCta}
                  </button>
                </div>
              </div>
              <aside className="plan-hero-aside" aria-label="Introductory pricing overview">
                <div className="plan-hero-card">
                  <p className="plan-hero-card-kicker">{pricingCopy.heroAsideKicker}</p>
                  <h3>{pricingCopy.heroAsideTitle}</h3>
                  <ul className="plan-hero-list">
                    {pricingCopy.heroAsidePoints.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
            ) : view === "app" && appPage === "profile" ? (
            <div className="hero-content-inner hero-content-with-profile">
              <div className="profile-hero-copy">
                <p className="eyebrow">{appCopy.profileHero.eyebrow}</p>
                <h1 className="profile-hero-title">{appCopy.profileHero.title}</h1>
                <p>{appCopy.profileHero.body}</p>
                <div className="hero-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setAppPage("dashboard");
                      scrollToSection("overview");
                    }}
                  >
                    ← {appCopy.profileHero.back}
                  </button>
                </div>
              </div>
              <aside className="profile-hero-aside" aria-label={appCopy.profileHero.asideAria}>
                <div className="profile-hero-card">
                  <p className="profile-hero-card-kicker">{appCopy.profileHero.asideKicker}</p>
                  <h3>{appCopy.profileHero.asideTitle}</h3>
                  <p>{appCopy.profileHero.asideBody}</p>
                </div>
              </aside>
            </div>
            ) : view === "app" && appPage === "payment-history" ? (
            <div className="hero-content-inner hero-content-with-profile">
              <div className="profile-hero-copy">
                <p className="eyebrow">{appCopy.paymentHero.eyebrow}</p>
                <h1 className="profile-hero-title">{appCopy.paymentHero.title}</h1>
                <p>{appCopy.paymentHero.body}</p>
                <div className="hero-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setAppPage("dashboard");
                      scrollToSection("overview");
                    }}
                  >
                    ← {appCopy.paymentHero.back}
                  </button>
                </div>
              </div>
              <aside className="profile-hero-aside" aria-label={appCopy.paymentHero.asideAria}>
                <div className="profile-hero-card">
                  <p className="profile-hero-card-kicker">{appCopy.paymentHero.asideKicker}</p>
                  <h3>{appCopy.paymentHero.asideTitle}</h3>
                  <p>{appCopy.paymentHero.asideBody}</p>
                </div>
              </aside>
            </div>
            ) : view === "app" && appPage === "admin-ops" ? (
            <div className="hero-content-inner hero-content-with-admin">
              <div className="admin-hero-copy">
                <p className="eyebrow">{appCopy.adminHero.eyebrow}</p>
                <h1>{appCopy.adminHero.title}</h1>
                <p>{appCopy.adminHero.body}</p>
                <div className="hero-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setAppPage("dashboard");
                      scrollToSection("overview");
                    }}
                  >
                    ← {appCopy.adminHero.back}
                  </button>
                </div>
              </div>
              <aside className="admin-hero-aside" aria-label={appCopy.adminHero.asideAria}>
                <div className="admin-hero-card">
                  <p className="admin-hero-card-kicker">{appCopy.adminHero.asideKicker}</p>
                  <h3>{appCopy.adminHero.asideTitle}</h3>
                  <ul className="admin-hero-list">
                    {appCopy.adminHero.asidePoints.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
            ) : view === "app" && appPage === "family" ? (
            <div className="hero-content-inner hero-content-with-family">
              <div className="family-hero-copy">
                <p className="eyebrow">{appCopy.familyHero.eyebrow}</p>
                <h1>{appCopy.familyHero.title}</h1>
                <p>{appCopy.familyHero.body}</p>
                <p className="family-hero-trust-banner">
                  {appCopy.familyHero.bannerStrong} {appCopy.familyHero.bannerTail}
                </p>
                <div className="hero-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setAppPage("dashboard");
                      scrollToSection("overview");
                    }}
                  >
                    ← {appCopy.familyHero.back}
                  </button>
                </div>
              </div>
              <aside className="family-hero-aside" aria-label={appCopy.familyHero.asideAria}>
                <div className="family-hero-card">
                  <p className="family-hero-card-kicker">{appCopy.familyHero.asideKicker}</p>
                  <h3>{appCopy.familyHero.asideTitle}</h3>
                  <ul className="family-hero-list">
                    {appCopy.familyHero.asidePoints.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
            ) : view === "app" && appPage === "verification" ? (
            <div className="hero-content-inner hero-content-with-verification">
              <div className="verification-hero-copy">
                <p className="eyebrow">{appCopy.verificationHero.eyebrow}</p>
                <h1>{appCopy.verificationHero.title}</h1>
                <p>{appCopy.verificationHero.body}</p>
                <div className="hero-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setAppPage("dashboard");
                      scrollToSection("overview");
                    }}
                  >
                    ← {appCopy.verificationHero.back}
                  </button>
                </div>
              </div>
              <aside className="verification-hero-aside" aria-label={appCopy.verificationHero.asideAria}>
                <div className="verification-hero-card">
                  <p className="verification-hero-card-kicker">{appCopy.verificationHero.asideKicker}</p>
                  <h3>{appCopy.verificationHero.asideTitle}</h3>
                  <ul className="verification-hero-list">
                    {appCopy.verificationHero.asidePoints.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
            ) : view === "app" && appPage === "create-will" ? (
            <div className="hero-content-inner hero-content-with-create-will">
              <div className="create-will-hero-copy">
                <p className="eyebrow">Planning &amp; documents</p>
                <h1>{appFamilyCopy.createWillPage.title}</h1>
                <p>{appFamilyCopy.createWillPage.body}</p>
                <div className="hero-actions">
                  <button type="button" onClick={returnToDashboard}>
                    ← {appFamilyCopy.createWillPage.backToDashboard}
                  </button>
                </div>
              </div>
              <aside className="create-will-hero-aside" aria-label={appFamilyCopy.createWillPage.asideTitle}>
                <div className="create-will-hero-card">
                  <p className="create-will-hero-kicker">{appFamilyCopy.createWillPage.asideTitle}</p>
                  <ul className="create-will-hero-points">
                    {appFamilyCopy.createWillPage.asidePoints.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
            ) : view === "app" && appPage === "media" ? (
            <>
              <p className="eyebrow">{appCopy.mediaHero.kicker}</p>
              <h1>{appCopy.mediaHero.title}</h1>
              <p>{appCopy.mediaHero.lead}</p>
              <div className="hero-actions">
                <button
                  type="button"
                  onClick={() =>
                    document
                      .getElementById("media-library-actions")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" })
                  }
                >
                  {appCopy.mediaHero.addMemory}
                </button>
                <button
                  type="button"
                  className="secondary"
                  onClick={() =>
                    document
                      .getElementById("media-gallery-browser")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" })
                  }
                >
                  {appCopy.mediaHero.openGallery}
                </button>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    setAppPage("dashboard");
                    scrollToSection("overview");
                  }}
                >
                  ← {appCopy.mediaHero.back}
                </button>
              </div>
            </>
            ) : view === "how" ? (
            <>
              <p className="eyebrow">{publicCopy.howPage.kicker}</p>
              <h1>{publicCopy.howPage.title}</h1>
              <p>{publicCopy.howPage.lead}</p>
              <div className="hero-actions">
                <button type="button" onClick={openGetStarted}>
                  {publicCopy.howPage.primaryCta}
                </button>
                <button type="button" className="secondary" onClick={openSecurityPage}>
                  {publicCopy.howPage.securityCta}
                </button>
              </div>
            </>
            ) : (
              <div
                className={
                  view === "app" && appPage === "dashboard"
                    ? "hero-content-inner hero-content-with-rotator"
                    : view === "app" && appPage === "admin-ops"
                      ? "hero-content-inner hero-content-with-admin"
                    : view === "auth"
                      ? "hero-content-inner hero-content-with-auth"
                    : view === "home"
                      ? "hero-content-inner hero-content-with-showcase"
                      : "hero-content-inner"
                }
              >
                <div className="hero-content-main">
                  <p className="eyebrow">{homeHeroCopy.eyebrow}</p>
                  <h1>{homeHeroCopy.title}</h1>
                  <p>{homeHeroCopy.body}</p>
                  <div className="hero-actions">
                    {authToken ? (
                      <>
                        {view !== "app" && (
                          <button
                            type="button"
                            onClick={() =>
                              loadFamilyTree(authToken, authUser).then(() => {
                                setAppPage("dashboard");
                                setView("app");
                              })
                            }
                          >
                            {navCopy.myDashboard}
                          </button>
                        )}
                        {view !== "app" ? (
                          <button type="button" className="secondary" onClick={signOut}>
                            {navCopy.signOut}
                          </button>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={openGetStarted}>
                          {homeHeroCopy.primaryCta}
                        </button>
                        <button type="button" className="secondary" onClick={openHowPage}>
                          {homeHeroCopy.secondaryCta}
                        </button>
                      </>
                    )}
                  </div>
                  {view === "home" ? (
                    <>
                      <p className="hero-support-note">{homeHeroCopy.support}</p>
                      {showHomeVideoSection ? (
                        <div className="hero-video-preview">
                          <div className="hero-video-preview-head">
                            <p className="hero-video-preview-kicker">{navCopy.how}</p>
                            <h2>{homeCopy.video.title}</h2>
                          </div>
                          <div className="hero-video-preview-frame">
                            {HOME_VIDEO_URL ? (
                              <video
                                className="hero-video-preview-player"
                                controls
                                preload="metadata"
                                playsInline
                                title={homeCopy.video.ariaLabel}
                                aria-label={homeCopy.video.ariaLabel}
                              >
                                <source src={HOME_VIDEO_URL} type="video/mp4" />
                              </video>
                            ) : showHomeVideoDevPlaceholder ? (
                              <div className="hero-video-preview-placeholder" role="note" aria-live="polite">
                                <span className="hero-video-preview-icon" aria-hidden="true">
                                  ▶
                                </span>
                                <div>
                                  <h3>{homeCopy.video.devPlaceholderTitle}</h3>
                                  <p>{homeCopy.video.devPlaceholderBody}</p>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
                {view === "auth" ? (
                  <aside className="auth-hero-aside" aria-label={appCopy.authHero.ariaLabel}>
                    <div className="auth-hero-card">
                      <p className="auth-hero-card-kicker">{appCopy.authHero.kicker}</p>
                      <h3>{appCopy.authHero.title}</h3>
                      <ul className="auth-hero-list">
                        {appCopy.authHero.points.map((point) => (
                          <li key={point}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </aside>
                ) : null}
                {view === "app" && appPage === "dashboard" && (
                  <HeroRotator copy={appCopy.heroRotator} />
                )}
                {view === "home" && (
                  <ProductHeroShowcase copy={publicCopy.productShowcase} />
                )}
              </div>
            )}
          </div>
        )}
      </header>

      {view === "plan" && (
        <section className="panel plan-page" aria-labelledby="plan-heading">
          {appNotice ? (
            <p className="dashboard-notice plan-page-notice" role="status">
              {appNotice}
            </p>
          ) : null}
          <div
            className="plan-promo-banner"
            role="region"
            aria-labelledby="plan-promo-title"
          >
            <div className="plan-promo-copy">
              <p id="plan-promo-title" className="plan-promo-title">
                {pricingCopy.promoTitle}
              </p>
              <p className="plan-promo-body">{pricingCopy.promoBody}</p>
              <p className="plan-promo-assurance">{pricingCopy.promoAssurance}</p>
            </div>
            <button
              type="button"
              className="plan-copy-link"
              onClick={copyPlanPageLink}
              aria-label={pricingCopy.copyLink}
            >
              {pricingCopy.copyLink}
            </button>
          </div>
          <section
            id="plan-value-section"
            className="plan-value-section"
            aria-labelledby="plan-value-title"
          >
            <div className="plan-section-heading">
              <h2 id="plan-value-title" className="plan-page-heading">
                {pricingCopy.valueTitle}
              </h2>
            </div>
            <div className="plan-value-grid">
              {pricingCopy.valueCards.map((card) => (
                <article key={card.title} className="plan-value-card">
                  <h3>{card.title}</h3>
                  <p>{card.body}</p>
                </article>
              ))}
            </div>
          </section>
          <h2 id="plan-heading" className="plan-page-heading">
            {pricingCopy.heading}
          </h2>
          <ul className="plan-tiles">
            {pricingCopy.tiles.map((tile) => (
              <li
                key={tile.name}
                className={`plan-tile${tile.featured ? " plan-tile-featured" : ""}${
                  selectedPlanId === tile.id ? " plan-tile-selected" : ""
                }`}
              >
                <div className="plan-tile-head">
                  <h3>{tile.name}</h3>
                  <span className="plan-tile-badge">{tile.badge}</span>
                </div>
                <PlanPricePromo
                  amount={tile.price}
                  periodLabel={tile.period}
                  planPrice={pricingCopy.planPrice}
                />
                <p className="plan-tile-lead">{tile.lead}</p>
                <ul className="plan-tile-features" aria-label={`${tile.name} includes`}>
                  {tile.features.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="plan-pay-cta"
                  onClick={() => selectPricingPlan(tile.id)}
                >
                  {tile.cta}
                </button>
              </li>
            ))}
          </ul>
          <p className="plan-comparison-note">{pricingCopy.comparisonNote}</p>
          <section className="plan-trust-section" aria-labelledby="plan-trust-title">
            <div className="plan-trust-card">
              <h3 id="plan-trust-title">{pricingCopy.trustTitle}</h3>
              <ul className="plan-trust-points">
                {pricingCopy.trustPoints.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </div>
          </section>
          {pricingCopy.trustFootnote ? (
            <p className="plan-trust-footnote">{pricingCopy.trustFootnote}</p>
          ) : null}
          {selectedPlan ? (
            <section className="plan-selection-card" aria-live="polite">
              <div className="plan-selection-head">
                <div>
                  <p className="plan-selection-kicker">{pricingCopy.enrollment.detailsTitle}</p>
                  <h3>{selectedPlan.name}</h3>
                  <p className="plan-selection-lead">{selectedPlan.lead}</p>
                </div>
                {selectedPlanSubscription ? (
                  <span className="plan-selection-badge">
                    {pricingCopy.enrollment.currentPlanBadge}
                  </span>
                ) : null}
              </div>

              {selectedPlanSubscription ? (
                <div className="plan-selection-success">
                  <h4>{pricingCopy.enrollment.successTitle}</h4>
                  <p>{pricingCopy.enrollment.successBody}</p>
                  <dl className="plan-selection-meta">
                    <div>
                      <dt>{pricingCopy.enrollment.renewalLabel}</dt>
                      <dd>{selectedPlanSubscription.checkoutLabel}</dd>
                    </div>
                    <div>
                      <dt>{pricingCopy.enrollment.startLabel}</dt>
                      <dd>{formatFriendlyDate(selectedPlanSubscription.startDate, locale)}</dd>
                    </div>
                    <div>
                      <dt>{pricingCopy.enrollment.expiryLabel}</dt>
                      <dd>{formatFriendlyDate(selectedPlanSubscription.expiryDate, locale)}</dd>
                    </div>
                  </dl>
                  <div className="plan-selection-actions">
                    <button
                      type="button"
                      onClick={() => {
                        persistSelectedPlan("");
                        setView("app");
                        setAppPage("profile");
                        setSelectedPlanId("");
                        setPlanEnrollmentResult(null);
                        setPlanEntrySource("");
                      }}
                    >
                      {pricingCopy.enrollment.viewProfile}
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => {
                        persistSelectedPlan("");
                        setPlanEnrollmentResult(null);
                        setSelectedPlanId("");
                        setPlanEntrySource("");
                      }}
                    >
                      {pricingCopy.enrollment.continueBrowsing}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <dl className="plan-selection-meta">
                    <div>
                      <dt>{pricingCopy.enrollment.renewalLabel}</dt>
                      <dd>{selectedPlan.checkoutLabel}</dd>
                    </div>
                    <div>
                      <dt>{pricingCopy.enrollment.startLabel}</dt>
                      <dd>{formatFriendlyDate(selectedPlanPreviewStart, locale)}</dd>
                    </div>
                    <div>
                      <dt>{pricingCopy.enrollment.expiryLabel}</dt>
                      <dd>{formatFriendlyDate(selectedPlanPreviewExpiry, locale)}</dd>
                    </div>
                  </dl>
                  <p className="plan-selection-note">{pricingCopy.enrollment.freeYearNote}</p>
                  <div className="plan-selection-grid">
                    <article className="plan-selection-included">
                      <h4>{pricingCopy.enrollment.includedTitle}</h4>
                      <ul>
                        {selectedPlan.features.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </article>
                    <article className="plan-selection-summary">
                      <p>{pricingCopy.enrollment.emailNotice}</p>
                      <p>
                        {authToken
                          ? pricingCopy.enrollment.signedInReady
                          : pricingCopy.enrollment.signInHelper}
                      </p>
                      {planEnrollmentError ? (
                        <p className="error-text plan-selection-error">{planEnrollmentError}</p>
                      ) : null}
                      <div className="plan-selection-actions">
                        {authToken ? (
                          <button
                            type="button"
                            onClick={enrollSelectedPlan}
                            disabled={planEnrollmentBusy}
                          >
                            {planEnrollmentBusy
                              ? `${pricingCopy.enrollment.enrollPrefix}...`
                              : `${pricingCopy.enrollment.enrollPrefix} ${selectedPlan.name}`}
                          </button>
                        ) : (
                          <>
                            <button type="button" onClick={openSignIn}>
                              {pricingCopy.enrollment.signInCta}
                            </button>
                            <button type="button" className="secondary" onClick={openGetStarted}>
                              {pricingCopy.enrollment.registerCta}
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => {
                            persistSelectedPlan("");
                            setSelectedPlanId("");
                            setPlanEnrollmentError("");
                            setPlanEnrollmentResult(null);
                            setPlanEntrySource("");
                          }}
                        >
                          {pricingCopy.enrollment.close}
                        </button>
                      </div>
                    </article>
                  </div>
                </>
              )}
            </section>
          ) : null}
        </section>
      )}

      {view === "terms" && (
        <section className="panel terms-page-panel" aria-labelledby="terms-top">
          {appNotice ? (
            <p className="dashboard-notice plan-page-notice" role="status">
              {appNotice}
            </p>
          ) : null}
          <TermsOfServicePage copy={termsCopy} locale={locale} />
        </section>
      )}

      {view === "security" && (
        <section className="panel public-resource-panel" aria-labelledby="security-page-title">
          <SecurityPrivacyPage
            copy={publicCopy.securityPage}
            onGetStarted={openGetStarted}
            onOpenHow={openHowPage}
            onOpenPricing={openPlanPage}
          />
        </section>
      )}

      {view === "how" && (
        <section className="panel public-resource-panel" aria-labelledby="how-page-title">
          <HowItWorksPage
            copy={publicCopy.howPage}
            onGetStarted={openGetStarted}
            onOpenPricing={openPlanPage}
            onOpenSecurity={openSecurityPage}
          />
        </section>
      )}

      {view === "home" && (
        <div className="home-section-stack">
          <section
            className="panel home-steps-panel"
            id="how-it-works"
            ref={setSectionRef("how-it-works")}
          >
            <div className="panel-head">
              <h2>{homeCopy.steps.title}</h2>
              <p>{homeCopy.steps.body}</p>
            </div>
            <div className="home-steps-grid">
              {homeCopy.steps.items.map((item) => (
                <article key={item.step} className="home-step-card">
                  <span className="home-step-number">{item.step}</span>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
            <div className="home-section-actions">
              <button type="button" onClick={openHowPage}>
                {homeCopy.steps.cta}
              </button>
            </div>
          </section>

          <section className="panel home-privacy-panel" id="protected-vault">
            <div className="home-privacy-layout">
              <div className="home-privacy-copy">
                <div className="panel-overview-head">
                  <p className="eyebrow panel-eyebrow-dark">{homeCopy.privacy.eyebrow}</p>
                  <h2>{homeCopy.privacy.title}</h2>
                  <p className="panel-lead">{homeCopy.privacy.body}</p>
                </div>
                <ul className="home-privacy-list">
                  {homeCopy.privacy.cards.map((item) => (
                    <li key={item.title} className="home-privacy-list-item">
                      <span className="home-card-icon" aria-hidden="true">
                        {item.icon}
                      </span>
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.body}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <aside className="home-privacy-aside" aria-label={homeCopy.privacy.title}>
                <div className="home-privacy-badge-stack">
                  {homeCopy.privacy.cards.map((item) => (
                    <span key={item.title} className="home-privacy-badge">
                      {item.title}
                    </span>
                  ))}
                </div>
              </aside>
            </div>
            <div className="home-section-actions">
              <button type="button" onClick={openSecurityPage}>
                {homeCopy.privacy.cta}
              </button>
            </div>
          </section>

          <section className="panel home-faq-panel" id="faq" ref={setSectionRef("faq")}>
            <div className="panel-head">
              <h2>{homeCopy.faq.title}</h2>
              <p>{homeCopy.faq.body}</p>
            </div>
            <div className="home-faq-list">
              {homeCopy.faq.items.slice(0, 3).map((item) => (
                <details key={item.question} className="home-faq-item">
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </section>

          <section className="panel home-cta-panel">
            <div className="home-cta-copy">
              <p className="eyebrow home-cta-eyebrow">{homeCopy.finalCta.eyebrow}</p>
              <h2>{homeCopy.finalCta.title}</h2>
              <p>{homeCopy.finalCta.body}</p>
            </div>
            <div className="home-section-actions">
              <button type="button" onClick={openGetStarted}>
                {homeCopy.finalCta.primaryCta}
              </button>
              <button type="button" className="secondary" onClick={openPlanPage}>
                {homeCopy.finalCta.secondaryCta}
              </button>
            </div>
          </section>
        </div>
      )}

      {view === "auth" && (
        <section className="panel auth-panel">
          <div className="auth-shell">
            <aside className="auth-aside">
              <p className="auth-aside-eyebrow">
                {authMode === "register"
                  ? authCopy.asideEyebrows.register
                  : authMode === "forgot"
                    ? authCopy.asideEyebrows.forgot
                    : authMode === "reset"
                      ? authCopy.asideEyebrows.reset
                      : authCopy.asideEyebrows.login}
              </p>
              <p className="auth-aside-tagline">
                {authMode === "register"
                  ? authCopy.asideTaglines.register
                  : authMode === "forgot" || authMode === "reset"
                    ? authCopy.asideTaglines.recovery
                    : authCopy.asideTaglines.login}
              </p>
              {(authMode === "login" || authMode === "register") && (
                <ul className="auth-aside-list">
                  {authCopy.bullets.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
            </aside>
            <div className="auth-card">
          <h2 className="auth-card-title">
            {authMode === "login" && authCopy.titles.login}
            {authMode === "register" && authCopy.titles.register}
            {authMode === "forgot" && authCopy.titles.forgot}
            {authMode === "reset" && authCopy.titles.reset}
          </h2>
          <p className="auth-card-lead">
            {authMode === "reset"
              ? authCopy.leads.reset
              : authMode === "forgot"
                ? authCopy.leads.forgot
                : authMode === "register"
                  ? authCopy.leads.register
                  : authCopy.leads.login}
          </p>
          {authSuccessBanner ? (
            <p className="helper-text" role="status">
              {authSuccessBanner}
            </p>
          ) : null}
          {authMode === "login" || authMode === "register" ? (
            <div className="verification-options auth-mode-switch">
              <button
                type="button"
                className={authMode === "login" ? "active" : ""}
                onClick={() => {
                  setAuthMode("login");
                  setMagicLinkHint("");
                  setMagicLinkDevUrl("");
                  setForgotHint("");
                  setForgotDevUrl("");
                  setAuthSuccessBanner("");
                }}
              >
                {authCopy.tabs.login}
              </button>
              <button
                type="button"
                className={authMode === "register" ? "active" : ""}
                onClick={() => {
                  setAuthMode("register");
                  setMagicLinkHint("");
                  setMagicLinkDevUrl("");
                  setForgotHint("");
                  setForgotDevUrl("");
                  setAuthSuccessBanner("");
                }}
              >
                {authCopy.tabs.register}
              </button>
            </div>
          ) : (
            <p className="auth-back-row">
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setAuthMode("login");
                  setForgotHint("");
                  setForgotDevUrl("");
                  setAuthError("");
                  setResetPasswordForm({ password: "", confirm: "" });
                }}
              >
                {authCopy.tabs.backToSignIn}
              </button>
            </p>
          )}
          {authMode === "forgot" ? (
            <form className="auth-form" onSubmit={requestForgotPassword} autoComplete="on">
              <label className="field-label" htmlFor="auth-email-forgot">
                {authCopy.labels.email}
              </label>
              <input
                id="auth-email-forgot"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                placeholder={authCopy.placeholders.email}
                value={authForm.email}
                onChange={(event) =>
                  setAuthForm((prev) => ({ ...prev, email: event.target.value }))
                }
              />
              <button type="submit" disabled={forgotBusy}>
                {forgotBusy ? authCopy.buttons.sending : authCopy.buttons.sendResetLink}
              </button>
              {forgotHint ? <p className="helper-text">{forgotHint}</p> : null}
              {forgotDevUrl ? (
                <p className="helper-text">
                  {authCopy.localDevReset}{" "}
                  <a href={forgotDevUrl} className="auth-magic-dev-link">
                    {authCopy.openResetLink}
                  </a>
                </p>
              ) : null}
            </form>
          ) : null}
          {authMode === "reset" ? (
            <form className="auth-form" onSubmit={submitResetPassword} autoComplete="on">
              <label className="field-label" htmlFor="reset-password">
                {authCopy.labels.newPassword}
              </label>
              <input
                id="reset-password"
                name="password"
                type="password"
                autoComplete="new-password"
                placeholder={authCopy.placeholders.newPassword}
                value={resetPasswordForm.password}
                onChange={(event) =>
                  setResetPasswordForm((prev) => ({
                    ...prev,
                    password: event.target.value,
                  }))
                }
              />
              <label className="field-label" htmlFor="reset-password-confirm">
                {authCopy.labels.confirmPassword}
              </label>
              <input
                id="reset-password-confirm"
                name="confirm"
                type="password"
                autoComplete="new-password"
                placeholder={authCopy.placeholders.confirmPassword}
                value={resetPasswordForm.confirm}
                onChange={(event) =>
                  setResetPasswordForm((prev) => ({
                    ...prev,
                    confirm: event.target.value,
                  }))
                }
              />
              <button type="submit" disabled={resetBusy}>
                {resetBusy ? authCopy.buttons.updating : authCopy.buttons.updatePassword}
              </button>
            </form>
          ) : null}
          {(authMode === "login" || authMode === "register") && GOOGLE_CLIENT_ID ? (
            <div className="auth-google">
              <p className="auth-google-divider">{authCopy.buttons.recommendedSignIn}</p>
              <div className={`auth-google-button-wrap ${googleBusy ? "is-busy" : ""}`}>
                <GoogleLogin
                  onSuccess={(res) => {
                    if (res.credential) {
                      handleGoogleCredential(res.credential);
                    }
                  }}
                  onError={() => setAuthError("Google sign-in was cancelled or failed.")}
                  useOneTap={false}
                  theme="outline"
                  size="large"
                  text="continue_with"
                  shape="pill"
                  width="100%"
                />
              </div>
              {googleBusy ? <p className="helper-text">{authCopy.buttons.signingIn}</p> : null}
            </div>
          ) : null}
          {(authMode === "login" || authMode === "register") && selectedPlan ? (
            <p className="helper-text auth-plan-helper">
              <strong>{selectedPlan.name}</strong>: {pricingCopy.enrollment.signInHelper}
            </p>
          ) : null}
          {(authMode === "login" || authMode === "register") && (
            <form className="auth-form" onSubmit={submitAuth} autoComplete="on">
              {GOOGLE_CLIENT_ID ? (
                <p className="auth-google-divider auth-email-divider">
                  {authCopy.buttons.orUseEmail}
                </p>
              ) : null}
              {authMode === "register" && (
                <>
                  <label className="field-label" htmlFor="auth-name">
                    {authCopy.labels.fullName}
                  </label>
                  <input
                    id="auth-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    placeholder={authCopy.placeholders.fullName}
                    value={authForm.name}
                    onChange={(event) =>
                      setAuthForm((prev) => ({ ...prev, name: event.target.value }))
                    }
                  />
                </>
              )}
              <label className="field-label" htmlFor="auth-email">
                {authCopy.labels.email}
              </label>
              <input
                id="auth-email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete={authMode === "login" ? "username" : "email"}
                autoCapitalize="none"
                spellCheck={false}
                placeholder={authCopy.placeholders.email}
                value={authForm.email}
                onChange={(event) =>
                  setAuthForm((prev) => ({ ...prev, email: event.target.value }))
                }
              />
              {authMode === "login" ? (
                <div className="auth-magic auth-magic-inline">
                  <p className="auth-magic-lead">{authCopy.magicLead}</p>
                  <button
                    type="button"
                    className="secondary"
                    onClick={requestMagicLink}
                    disabled={magicLinkBusy}
                  >
                    {magicLinkBusy
                      ? authCopy.buttons.sendingMagicLink
                      : authCopy.buttons.emailMagicLink}
                  </button>
                  {magicLinkHint ? <p className="helper-text">{magicLinkHint}</p> : null}
                  {magicLinkDevUrl ? (
                    <p className="helper-text">
                      {authCopy.localDevSignIn}{" "}
                      <a href={magicLinkDevUrl} className="auth-magic-dev-link">
                        {authCopy.openSignInLink}
                      </a>
                    </p>
                  ) : null}
                </div>
              ) : null}
              <label className="field-label" htmlFor="auth-password">
                {authCopy.labels.password}
              </label>
              <input
                id="auth-password"
                name="password"
                type="password"
                autoComplete={authMode === "login" ? "current-password" : "new-password"}
                placeholder={
                  authMode === "login"
                    ? authCopy.placeholders.loginPassword
                    : authCopy.placeholders.registerPassword
                }
                value={authForm.password}
                onChange={(event) =>
                  setAuthForm((prev) => ({ ...prev, password: event.target.value }))
                }
              />
              <button type="submit">
                {authMode === "login" ? authCopy.buttons.login : authCopy.buttons.register}
              </button>
              {authMode === "login" ? (
                <p className="auth-magic-lead auth-forgot-inline">
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setAuthMode("forgot");
                      setAuthError("");
                      setForgotHint("");
                      setForgotDevUrl("");
                      setMagicLinkHint("");
                      setMagicLinkDevUrl("");
                    }}
                  >
                    {authCopy.buttons.forgotPassword}
                  </button>
                </p>
              ) : null}
            </form>
          )}
          {authError && <p className="error-text">{authError}</p>}
            </div>
          </div>
        </section>
      )}

      {view === "app" && appPage === "dashboard" && (
        <>
          {authUser && (
            <section
              className="panel panel-welcome-dashboard"
              id="overview"
              ref={setSectionRef("overview")}
            >
              <div className="welcome-dashboard-row">
                <div className="welcome-dashboard-text">
                  <h2>{appCopy.dashboard.welcome(authUser.name)}</h2>
                  <p className="helper-text">{authUser.email}</p>
                </div>
                <button type="button" className="welcome-gallery-cta" onClick={openPhotoMemoryGallery}>
                  {appCopy.dashboard.openMediaGallery}
                </button>
              </div>
            </section>
          )}
          {appNotice ? (
            <p className="dashboard-notice" role="status">
              {appNotice}
            </p>
          ) : null}

          <section className="card-grid" id="features" ref={setSectionRef("features")}>
            {featureCards.map((card) => {
              const { id, title, description } = card;
              const isSelected = selectedFeatureCard === id;
              return (
                <article
                  className={`card card-selectable ${isSelected ? "card-selected" : ""}`}
                  key={id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  aria-label={title}
                  onClick={() => {
                    if (id === "create-will") {
                      openCreateWillPage();
                      return;
                    }
                    setSelectedFeatureCard((prev) => (prev === id ? null : id));
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      if (id === "create-will") {
                        openCreateWillPage();
                        return;
                      }
                      setSelectedFeatureCard((prev) => (prev === id ? null : id));
                    }
                  }}
                >
                  <div className="card-selectable-head">
                    <h2 className="card-title">{title}</h2>
                    {isSelected ? (
                      <span className="card-selected-pill" aria-hidden>
                        {appCopy.featureCards.active}
                      </span>
                    ) : null}
                  </div>
                  <p className="card-description">{description}</p>
                  {id === "private-journals" ? (
                    <p className="card-cta">
                      {isSelected
                        ? appCopy.featureCards.privateJournals.open
                        : appCopy.featureCards.privateJournals.closed}
                    </p>
                  ) : id === "photo-memory-collage" ? (
                    <p className="card-cta">
                      {isSelected
                        ? appCopy.featureCards.photoMemoryCollage.open
                        : appCopy.featureCards.photoMemoryCollage.closed}
                    </p>
                  ) : id === "create-will" ? (
                    <p className="card-cta">{appFamilyCopy.createWillCard.cta}</p>
                  ) : (
                    <p className={`card-cta ${isSelected ? "" : "card-cta-muted"}`}>
                      {isSelected
                        ? appCopy.featureCards.previewOpen
                        : appCopy.featureCards.previewClosed}
                    </p>
                  )}
                </article>
              );
            })}
          </section>

          <div id="journal" ref={setSectionRef("journal")} className="feature-workspace">
            {selectedFeatureCard ? (
              selectedFeatureCard === "private-journals" ? (
                <JournalPanel
                  authToken={authToken}
                  selectedRecipient={selectedRecipient}
                  onRecipientChange={setSelectedRecipient}
                  recipientOptions={familyMembers
                    .filter((m) => m.relation !== "Owner")
                    .map((m) => ({
                      ...m,
                      relation: appRelationLabel(m.relation, appCopy.trustedPeople),
                    }))}
                  initialOpenJournalId={requestedJournalId}
                  locale={locale}
                />
              ) : selectedFeatureCard === "family-confessions" ? (
                <FamilyConfessionsPanel
                  authToken={authToken}
                  recipientOptions={familyMembers
                    .filter((m) => m.relation !== "Owner")
                    .map((m) => ({
                      ...m,
                      relation: appRelationLabel(m.relation, appCopy.trustedPeople),
                    }))}
                  locale={locale}
                  onOpenLinkedJournal={(journalId) => {
                    setRequestedJournalId(journalId);
                    setSelectedFeatureCard("private-journals");
                    scrollToSection("journal");
                  }}
                />
              ) : selectedFeatureCard === "photo-memory-collage" ? (
                <MediaGalleryPanel
                  authToken={authToken}
                  locale={locale}
                  reloadNonce={mediaGalleryTick}
                  tagOptions={familyMembers.map((m) => ({
                    id: String(m.backendNodeId || m.id),
                    label: `${m.name}${m.nickname ? ` “${m.nickname}”` : ""} (${appRelationLabel(m.relation, appCopy.trustedPeople)})`,
                  }))}
                />
              ) : (
                <section className="panel feature-workspace-placeholder">
                  <h2>{appCopy.dashboard.placeholderTitle}</h2>
                  <p>{appCopy.dashboard.placeholderBody}</p>
                  <p className="helper-text">
                    {appCopy.dashboard.placeholderHelperPrefix}{" "}
                    <strong>{appCopy.dashboard.placeholderHelperStrong}</strong>{" "}
                    {appCopy.dashboard.placeholderHelperSuffix}
                  </p>
                </section>
              )
            ) : (
              <p className="feature-workspace-hint">
                {appCopy.dashboard.selectHint}
              </p>
            )}
          </div>
        </>
      )}

      {view === "app" && appPage === "family" && (
        <div className="app-page app-family-page">
          {appNotice ? (
            <p className="dashboard-notice" role="status">
              {appNotice}
            </p>
          ) : null}
          <section className="family-page-stack" id="family" ref={setSectionRef("family")}>
            <TrustedPeopleBuilderPanel
              familyMembers={familyMembers}
              familyInvitations={familyInvitations}
              memberName={memberName}
              setMemberName={setMemberName}
              memberRelation={memberRelation}
              setMemberRelation={setMemberRelation}
              memberEmail={memberEmail}
              setMemberEmail={setMemberEmail}
              memberBirthYear={memberBirthYear}
              setMemberBirthYear={setMemberBirthYear}
              memberNickname={memberNickname}
              setMemberNickname={setMemberNickname}
              memberPronouns={memberPronouns}
              setMemberPronouns={setMemberPronouns}
              memberIdentityNote={memberIdentityNote}
              setMemberIdentityNote={setMemberIdentityNote}
              memberSpecialNote={memberSpecialNote}
              setMemberSpecialNote={setMemberSpecialNote}
              memberCoParentId={memberCoParentId}
              setMemberCoParentId={setMemberCoParentId}
              memberPartnerId={memberPartnerId}
              setMemberPartnerId={setMemberPartnerId}
              memberChartParentId={memberChartParentId}
              setMemberChartParentId={setMemberChartParentId}
              childBranchOptions={childBranchOptions}
              partnerLinkOptions={partnerLinkOptions}
              addFamilyMember={addFamilyMember}
              requestDeleteFamilyMember={requestDeleteFamilyMember}
              askToJoin={askToJoin}
              editingFamilyEmailNodeId={editingFamilyEmailNodeId}
              editingFamilyEmailValue={editingFamilyEmailValue}
              setEditingFamilyEmailValue={setEditingFamilyEmailValue}
              familyEmailSavingId={familyEmailSavingId}
              beginFamilyEmailEdit={beginFamilyEmailEdit}
              cancelFamilyEmailEdit={cancelFamilyEmailEdit}
              saveFamilyMemberEmail={saveFamilyMemberEmail}
              familyInviteBusyId={familyInviteBusyId}
              familyInvitationBusyId={familyInvitationBusyId}
              resendInvite={resendFamilyInvite}
              cancelInvite={cancelFamilyInvite}
              acceptInvitation={acceptFamilyInvitation}
              planLimitPrompt={familyPlanLimitPrompt}
              onStartTrial={startTrialFromFamilyLimit}
              onOpenPricing={openPlanPage}
              planLimitCopy={familyPlanLimitCopy}
            />

            <div className="panel chart-panel family-chart-panel">
              <div className="panel-head family-panel-head">
                <div className="family-panel-copy">
                  <p className="family-chart-label">{appCopy.familyChart.label}</p>
                  <h2>{appCopy.familyChart.title}</h2>
                  <p className="chart-panel-lead">{appCopy.familyChart.lead}</p>
                </div>
                <div className="family-panel-summary" aria-label={appCopy.familyChart.summaryAria}>
                  <span>{appCopy.familyChart.summaryParents}</span>
                  <span>{appCopy.familyChart.summaryTap}</span>
                  <span>{appCopy.familyChart.summaryDrag}</span>
                </div>
              </div>
              <div className="family-chart-canvas-wrap">
                <svg
                  ref={svgRef}
                  viewBox={`0 0 ${familyChartViewWidth} ${familyChartViewHeight}`}
                  role="img"
                  aria-label={appCopy.familyChart.svgAria}
                  onPointerDown={() => {
                    setSelectedNodeId(null);
                    setHoveredChartMemberId(null);
                  }}
                  style={{ width: familyChartViewWidth, minWidth: "100%", maxWidth: "none" }}
                >
                  <g className="chart-edges" pointerEvents="none">
                  {(() => {
                    const ownerIdx = familyMembers.findIndex((m) => m.relation === "Owner");
                    const ownerMember = ownerIdx >= 0 ? familyMembers[ownerIdx] : null;
                    const ownerL = ownerMember ? getFamilyChartLayout(ownerMember, ownerIdx) : null;
                    const lines = [];
                    if (ownerL && ownerMember) {
                      familyMembers.forEach((member, index) => {
                        if (member.relation !== "Parent") {
                          return;
                        }
                        const from = getFamilyChartLayout(member, index);
                        const emphasize =
                          hoveredChartMemberId === member.id ||
                          hoveredChartMemberId === ownerMember.id ||
                          selectedNodeId === member.id ||
                          selectedNodeId === ownerMember.id;
                        lines.push(
                          <path
                            key={`parent-to-owner-${member.id}`}
                            d={buildChartBranchPath(from, ownerL)}
                            className={`chart-edge-branch${emphasize ? " is-emphasis" : ""}`}
                          />
                        );
                      });

                      familyMembers.forEach((member, index) => {
                        if (member.relation !== "Child") {
                          return;
                        }
                        const to = getFamilyChartLayout(member, index);
                        const emphasize =
                          hoveredChartMemberId === member.id ||
                          hoveredChartMemberId === ownerMember.id ||
                          selectedNodeId === member.id ||
                          selectedNodeId === ownerMember.id;
                        lines.push(
                          <path
                            key={`owner-to-child-${member.id}`}
                            d={buildChartBranchPath(ownerL, to)}
                            className={`chart-edge-branch${emphasize ? " is-emphasis" : ""}`}
                          />
                        );
                        if (member.coParentNodeId) {
                          const coParentMember = familyMembers.find(
                            (candidate) => candidate.backendNodeId === member.coParentNodeId
                          );
                          if (coParentMember) {
                            const coParentIndex = familyMembers.indexOf(coParentMember);
                            const coParentLayout = getFamilyChartLayout(coParentMember, coParentIndex);
                            const coParentEmphasis =
                              emphasize ||
                              hoveredChartMemberId === coParentMember.id ||
                              selectedNodeId === coParentMember.id;
                            lines.push(
                              <path
                                key={`coparent-to-child-${member.id}`}
                                d={buildChartBranchPath(coParentLayout, to)}
                                className={`chart-edge-branch chart-edge-branch-secondary${
                                  coParentEmphasis ? " is-emphasis" : ""
                                }`}
                              />
                            );
                          }
                        }
                      });
                    }
                    familyMembers.forEach((m, i) => {
                      if (m.relation !== "Grandchild") {
                        return;
                      }
                      const pk = chartParentKeyFromMember(m);
                      if (!pk || pk === "owner-node") {
                        return;
                      }
                      const p = familyMembers.find(
                        (x) => x.backendNodeId === pk
                      );
                      if (!p) {
                        return;
                      }
                      const pi = familyMembers.indexOf(p);
                      const from = getFamilyChartLayout(p, pi);
                      const to = getFamilyChartLayout(m, i);
                      const emphasize =
                        hoveredChartMemberId === m.id ||
                        hoveredChartMemberId === p.id ||
                        selectedNodeId === m.id ||
                        selectedNodeId === p.id;
                      lines.push(
                        <path
                          key={`tree-branch-${m.id}`}
                          d={buildChartBranchPath(from, to)}
                          className={`chart-edge-branch${emphasize ? " is-emphasis" : ""}`}
                        />
                      );
                    });
                    return lines;
                  })()}
                  </g>
                  {familyMembers.map((member, index) => {
                  const { x, y } = getFamilyChartLayout(member, index);
                  const isSelected = selectedNodeId === member.id;
                  const relationTone = chartMemberTone(member);
                  const initials = chartMemberInitials(member.name);
                  const coParentMember = member.coParentNodeId
                    ? familyMembers.find((m) => m.backendNodeId === member.coParentNodeId)
                    : null;
                  const companionLabel =
                    member.relation === "Child" && coParentMember
                      ? appCopy.familyChart.otherParent(coParentMember.name)
                      : "";
                  return (
                    <g
                      key={`node-${member.id}`}
                      className={`chart-node chart-node-${relationTone}${
                        isSelected ? " selected" : ""
                      }${hoveredChartMemberId === member.id ? " is-hovered" : ""}`}
                      onPointerDown={(e) => onNodePointerDown(e, member.id)}
                      onPointerMove={onNodePointerMove}
                      onPointerUp={onNodePointerUp}
                      onPointerCancel={onNodePointerUp}
                      onMouseEnter={() => setHoveredChartMemberId(member.id)}
                      onMouseLeave={() =>
                        setHoveredChartMemberId((current) =>
                          current === member.id ? null : current
                        )
                      }
                      aria-label={`${member.name}, ${member.relation}`}
                    >
                      <rect
                        x={x}
                        y={y}
                        rx="20"
                        ry="20"
                        width={CHART_CARD_W}
                        height={CHART_CARD_H}
                        className="chart-node-hitbox"
                      />
                      <foreignObject
                        x={x}
                        y={y}
                        width={CHART_CARD_W}
                        height={CHART_CARD_H}
                        pointerEvents="none"
                      >
                        <div
                          xmlns="http://www.w3.org/1999/xhtml"
                          className={`chart-node-card chart-node-card--${relationTone}${isSelected ? " is-selected" : ""}`}
                        >
                          <div className="chart-node-card__top">
                            <span className="chart-node-card__avatar">{initials}</span>
                            <div className="chart-node-card__identity">
                              <p className="chart-node-card__name">{member.name}</p>
                            </div>
                            <span className="chart-node-card__role">
                              {member.relation === "Owner"
                                ? appCopy.trustedPeople.ownerRelation
                                : member.relation === "Parent"
                                  ? appCopy.trustedPeople.relationshipOptions[0]
                                  : member.relation === "Spouse"
                                    ? appCopy.trustedPeople.relationshipOptions[1]
                                    : member.relation === "Partner"
                                      ? appCopy.trustedPeople.relationshipOptions[2]
                                      : member.relation === "Ex-spouse"
                                        ? appCopy.trustedPeople.relationshipOptions[3]
                                        : member.relation === "Sibling"
                                          ? appCopy.trustedPeople.relationshipOptions[4]
                                          : member.relation === "Child"
                                            ? appCopy.trustedPeople.relationshipOptions[5]
                                            : member.relation === "Grandchild"
                                              ? appCopy.trustedPeople.relationshipOptions[6]
                                              : member.relation === "Friend"
                                                ? appCopy.trustedPeople.relationshipOptions[7]
                                                : member.relation === "Legal Advisor"
                                                  ? appCopy.trustedPeople.relationshipOptions[8]
                                                  : member.relation}
                            </span>
                          </div>
                          {companionLabel ? (
                            <div className="chart-node-card__footer">
                              <span className="chart-node-card__companion">
                                {companionLabel.length > 26
                                  ? `${companionLabel.slice(0, 26)}…`
                                  : companionLabel}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </foreignObject>
                    </g>
                  );
                  })}
                </svg>
              </div>
              {activeChartMember ? (
                <div className="chart-focus-card" aria-live="polite">
                  <div className="chart-focus-card__copy">
                    <span className="chart-focus-card__eyebrow">
                      {appCopy.familyChart.focusedEyebrow}
                    </span>
                    <h3>{activeChartMember.name}</h3>
                    <p>
                      {activeChartMember.relation === "Owner"
                        ? appCopy.familyChart.ownerFocus
                        : appCopy.familyChart.memberFocus
                            .replace("{name}", activeChartMember.name)
                            .replace("{relation}", activeChartMember.relation.toLowerCase())}
                    </p>
                  </div>
                  <div className="chart-focus-card__meta">
                    <span>{chartMemberAccessLabel(activeChartMember, appCopy.familyChart)}</span>
                    {(() => {
                      const parentKey = chartParentKeyFromMember(activeChartMember);
                      const parentMember =
                        parentKey && parentKey !== "owner-node"
                          ? familyMembers.find((m) => m.backendNodeId === parentKey)
                          : null;
                      if (activeChartMember.relation === "Owner") {
                        return <span>{appCopy.familyChart.centerOfPlan}</span>;
                      }
                      if (activeChartMember.relation === "Parent") {
                        return <span>{appCopy.familyChart.aboveYou}</span>;
                      }
                      if (activeChartMember.relation === "Sibling") {
                        return <span>{appCopy.familyChart.sameGeneration}</span>;
                      }
                      if (activeChartMember.relation === "Child") {
                        return <span>{appCopy.familyChart.belowYou}</span>;
                      }
                      if (activeChartMember.relation === "Grandchild" && parentMember) {
                        return <span>{appCopy.familyChart.belowParent(parentMember.name)}</span>;
                      }
                      if (activeChartMember.relation === "Spouse" || activeChartMember.relation === "Partner") {
                        return (
                          <span>
                            {activeChartMember.partnerNodeId === "owner-node"
                              ? appCopy.familyChart.besideYou
                              : appCopy.familyChart.besidePartner}
                          </span>
                        );
                      }
                      if (parentMember) {
                        return <span>{appCopy.familyChart.branchUnder(parentMember.name)}</span>;
                      }
                      return <span>{appCopy.familyChart.familyMember}</span>;
                    })()}
                    {(() => {
                      const coParentMember = activeChartMember.coParentNodeId
                        ? familyMembers.find((m) => m.backendNodeId === activeChartMember.coParentNodeId)
                        : null;
                      if (coParentMember) {
                        return <span>{appCopy.familyChart.otherParent(coParentMember.name)}</span>;
                      }
                      if (activeChartMember.specialNote) {
                        return (
                          <span>
                            {activeChartMember.specialNote.length > 64
                              ? `${activeChartMember.specialNote.slice(0, 64)}…`
                              : activeChartMember.specialNote}
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        </div>
      )}

      {view === "app" && appPage === "admin-ops" && authToken && (
        <div className="app-page app-admin-page">
          <AdminOperationsPage authToken={authToken} />
        </div>
      )}

      {view === "app" && appPage === "media" && (
        <div className="app-page app-media-page">
          <div className="app-media-page-stack" id="media" ref={setSectionRef("media")}>
            <section className="panel media-usage-panel media-vault-hero-panel">
              <div className="media-vault-hero-copy">
                <p className="media-vault-kicker">{appCopy.mediaHero.kicker}</p>
                <h2>{appCopy.mediaHero.title}</h2>
                <p className="media-vault-lead">{appCopy.mediaHero.lead}</p>
                <div className="media-vault-badges" aria-label={appCopy.mediaHero.badgesAria}>
                  <span className="media-vault-badge">{appCopy.mediaHero.badgeEncrypted}</span>
                  <span className="media-vault-badge">{appCopy.mediaHero.badgeRelease}</span>
                  <span className="media-vault-badge">{appCopy.mediaHero.badgeTrusted}</span>
                </div>
                <div className="media-vault-cta-row">
                  <button
                    type="button"
                    onClick={() =>
                      document
                        .getElementById("media-library-actions")
                        ?.scrollIntoView({ behavior: "smooth", block: "start" })
                    }
                  >
                    {appCopy.mediaHero.addMemory}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() =>
                      document
                        .getElementById("media-gallery-browser")
                        ?.scrollIntoView({ behavior: "smooth", block: "start" })
                    }
                  >
                    {appCopy.mediaHero.openGallery}
                  </button>
                </div>
                {mediaStats.notice ? (
                  <p className="dashboard-notice" role="status">
                    {mediaStats.notice}
                  </p>
                ) : null}
              </div>
              <div className="media-vault-usage-card">
                {!mediaStats.loaded ? (
                  <p className="helper-text">{appCopy.mediaHero.loading}</p>
                ) : (
                  <>
                    <p className="media-vault-usage-kicker">{appCopy.mediaHero.usageKicker}</p>
                    {mediaStats.totalBytes / Math.max(mediaStats.quotaBytes, 1) < 0.7 ? (
                      <p className="media-vault-usage-message">
                        {appCopy.mediaHero.usagePlenty}
                      </p>
                    ) : mediaStats.totalBytes >= mediaStats.quotaBytes ? (
                      <p className="media-vault-usage-message media-vault-usage-message-warning">
                        {appCopy.mediaHero.usageWarning}
                      </p>
                    ) : (
                      <p className="media-vault-usage-message">
                        {appCopy.mediaHero.usageReview}
                      </p>
                    )}
                    <details className="media-vault-usage-details">
                      <summary>{appCopy.mediaHero.viewStorageDetails}</summary>
                      <dl className="media-usage-dl">
                        <div>
                          <dt>{appCopy.mediaHero.memories}</dt>
                          <dd>{mediaStats.totalCount}</dd>
                        </div>
                        <div>
                          <dt>{appCopy.mediaHero.spaceUsed}</dt>
                          <dd>{formatStorageBytes(mediaStats.totalBytes)}</dd>
                        </div>
                        <div>
                          <dt>{appCopy.mediaHero.planSpace}</dt>
                          <dd>{formatStorageBytes(mediaStats.quotaBytes)}</dd>
                        </div>
                        <div>
                          <dt>{appCopy.mediaHero.spaceLeft}</dt>
                          <dd>
                            {formatStorageBytes(
                              Math.max(0, mediaStats.quotaBytes - mediaStats.totalBytes)
                            )}
                          </dd>
                        </div>
                      </dl>
                      {(mediaStats.totalBytes / Math.max(mediaStats.quotaBytes, 1)) * 100 >= 70 ? (
                        <>
                          <label className="field-label" htmlFor="media-usage-meter">
                            {appCopy.mediaHero.spaceUsed}
                          </label>
                          <div id="media-usage-meter" className="meter" aria-hidden>
                            <div
                              style={{
                                width: `${Math.min(
                                  (mediaStats.totalBytes / Math.max(mediaStats.quotaBytes, 1)) *
                                    100,
                                  100
                                )}%`,
                              }}
                            />
                          </div>
                        </>
                      ) : null}
                    </details>
                  </>
                )}
              </div>
            </section>

            <MediaGalleryPanel
              authToken={authToken}
              locale={locale}
              reloadNonce={mediaGalleryTick}
              tagOptions={familyMembers.map((m) => ({
                id: String(m.backendNodeId || m.id),
                label: `${m.name}${m.nickname ? ` “${m.nickname}”` : ""} (${appRelationLabel(m.relation, appCopy.trustedPeople)})`,
              }))}
              onMediaLibraryChanged={loadMediaStats}
            />
          </div>
        </div>
      )}

      {view === "app" && appPage === "create-will" && (
        <div className="app-page app-create-will-page">
          <WillPreparationPanel authToken={authToken} locale={locale} />
        </div>
      )}

      {view === "app" && appPage === "verification" && (
        <div className="app-page app-verification-page">
          <section className="panel" id="verification" ref={setSectionRef("verification")}>
            <h2>{appCopy.verificationWorkflow.heading}</h2>
            <p>{appCopy.verificationWorkflow.lead}</p>
            <div className="verification-options">
              {verificationOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={option.id === verificationId ? "active" : ""}
                  onClick={() => setVerificationId(option.id)}
                >
                  {option.title}
                </button>
              ))}
            </div>
            <article className="verification-card">
              <h3>{selectedVerification?.title}</h3>
              <p>{selectedVerification?.description}</p>
              <p className="helper-text">
                {appCopy.verificationWorkflow.offlineStatus}:{" "}
                <strong>{appCopy.verificationWorkflow.manualReviewRequired}</strong>
              </p>
            </article>
          </section>
        </div>
      )}

      {view === "app" && appPage === "profile" && authToken && (
        <MyProfilePanel
          authToken={authToken}
          authUser={authUser}
          locale={locale}
          onProfileSaved={handleProfileSaved}
        />
      )}

      {view === "app" && appPage === "payment-history" && authToken && (
        <PaymentHistoryPanel authToken={authToken} locale={locale} copy={appCopy.paymentHistory} />
      )}

      {changePasswordOpen ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            if (!changePasswordBusy) {
              setChangePasswordOpen(false);
              setChangePasswordMessage("");
            }
          }}
        >
          <div
            className="modal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="change-pw-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="change-pw-title">{appCopy.modals.changePassword.title}</h2>
            <p className="modal-lead">
              {appCopy.modals.changePassword.lead}
            </p>
            <form className="modal-form" onSubmit={submitChangePassword}>
              <label className="field-label" htmlFor="cp-current">
                {appCopy.modals.changePassword.currentPassword}
              </label>
              <input
                id="cp-current"
                name="currentPassword"
                type="password"
                autoComplete="current-password"
                value={changePasswordForm.currentPassword}
                onChange={(event) =>
                  setChangePasswordForm((prev) => ({
                    ...prev,
                    currentPassword: event.target.value,
                  }))
                }
              />
              <label className="field-label" htmlFor="cp-new">
                {appCopy.modals.changePassword.newPassword}
              </label>
              <input
                id="cp-new"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                value={changePasswordForm.newPassword}
                onChange={(event) =>
                  setChangePasswordForm((prev) => ({
                    ...prev,
                    newPassword: event.target.value,
                  }))
                }
              />
              <label className="field-label" htmlFor="cp-confirm">
                {appCopy.modals.changePassword.confirmNewPassword}
              </label>
              <input
                id="cp-confirm"
                name="confirm"
                type="password"
                autoComplete="new-password"
                value={changePasswordForm.confirm}
                onChange={(event) =>
                  setChangePasswordForm((prev) => ({
                    ...prev,
                    confirm: event.target.value,
                  }))
                }
              />
              {changePasswordMessage ? (
                <p
                  className={
                    changePasswordFeedbackError
                      ? "modal-feedback modal-feedback-error"
                      : "modal-feedback modal-feedback-success"
                  }
                >
                  {changePasswordMessage}
                </p>
              ) : null}
              <div className="modal-actions">
                <button type="submit" disabled={changePasswordBusy}>
                  {changePasswordBusy
                    ? appCopy.modals.changePassword.saving
                    : appCopy.modals.changePassword.save}
                </button>
                <button
                  type="button"
                  className="secondary"
                  disabled={changePasswordBusy}
                  onClick={() => {
                    setChangePasswordOpen(false);
                    setChangePasswordMessage("");
                  }}
                >
                  {appCopy.modals.changePassword.cancel}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <SiteFooter
        copy={publicCopy.footer}
        onOpenTerms={openTermsPage}
        onOpenProduct={() => openHomeSection("product")}
        onOpenHow={openHowPage}
        onOpenSecurity={openSecurityPage}
        onOpenPricing={openPlanPage}
        onOpenFaq={() => openHomeSection("faq")}
        onGetStarted={openGetStarted}
      />
    </main>

      {upgradeReminderOpen && authToken ? (
        <aside
          className="upgrade-reminder-toast"
          role="region"
          aria-labelledby="upgrade-reminder-title"
        >
          <button
            type="button"
            className="upgrade-reminder-close"
            onClick={dismissUpgradeReminder}
            aria-label={upgradeReminderCopy.secondaryCta}
          >
            ×
          </button>
          <p className="upgrade-reminder-badge">{upgradeReminderCopy.badge}</p>
          <h2 id="upgrade-reminder-title">{upgradeReminderCopy.title}</h2>
          <p className="upgrade-reminder-body">{upgradeReminderCopy.body}</p>
          <p className="upgrade-reminder-helper">{upgradeReminderCopy.helper}</p>
          <div className="upgrade-reminder-actions">
            <button type="button" onClick={openPlanPageFromReminder}>
              {upgradeReminderCopy.primaryCta}
            </button>
            <button type="button" className="secondary" onClick={dismissUpgradeReminder}>
              {upgradeReminderCopy.secondaryCta}
            </button>
          </div>
        </aside>
      ) : null}

      {sessionWarningOpen && authToken ? (
        <div
          className="modal-backdrop session-timeout-backdrop"
          role="presentation"
          data-session-tick={sessionClock}
        >
          <div
            className="modal-dialog session-timeout-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="session-timeout-title"
            aria-describedby="session-timeout-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="session-timeout-title">{appCopy.modals.sessionEnding.title}</h2>
            <p id="session-timeout-desc" className="modal-lead">
              {appCopy.modals.sessionEnding.leadPrefix}
              {sessionSecondsLeft > 0 ? (
                <>
                  {" "}
                  {appCopy.modals.sessionEnding.timeRemaining}{" "}
                  <strong>
                    {sessionSecondsLeft < 60
                      ? appCopy.modals.sessionEnding.oneMinute
                      : appCopy.modals.sessionEnding.manyMinutes(
                          Math.ceil(sessionSecondsLeft / 60)
                        )}
                  </strong>
                  .
                </>
              ) : null}
            </p>
            <p className="modal-lead session-timeout-second-line">
              {appCopy.modals.sessionEnding.secondLine}
            </p>
            {sessionExtendError ? (
              <p className="modal-feedback modal-feedback-error">{sessionExtendError}</p>
            ) : null}
            <div className="modal-actions session-timeout-actions">
              <button type="button" onClick={extendSession} disabled={sessionExtendBusy}>
                {sessionExtendBusy
                  ? appCopy.modals.sessionEnding.extending
                  : appCopy.modals.sessionEnding.extend}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={signOut}
                disabled={sessionExtendBusy}
              >
                {appCopy.modals.sessionEnding.signOut}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {familyDeleteConfirm ? (
        <div
          className="modal-backdrop family-member-delete-backdrop"
          role="presentation"
          onClick={cancelDeleteFamilyMember}
        >
          <div
            className="modal-dialog family-member-delete-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="family-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="family-delete-title">{appCopy.modals.familyDelete.title}</h2>
            <p className="modal-lead">
              {appCopy.modals.familyDelete.lead(familyDeleteConfirm.name)}
            </p>
            <div className="modal-actions family-member-delete-actions">
              <button
                type="button"
                className="family-delete-confirm-btn"
                onClick={confirmDeleteFamilyMember}
                disabled={familyDeleting}
              >
                {familyDeleting
                  ? appCopy.modals.familyDelete.removing
                  : appCopy.modals.familyDelete.remove}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={cancelDeleteFamilyMember}
                disabled={familyDeleting}
              >
                {appCopy.modals.familyDelete.cancel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

    {showGuestContact ? <GuestContactWidget apiBase={API_BASE} /> : null}
    {authToken ? (
      <AfterMeGuideChat
        apiBase={API_BASE}
        authToken={authToken}
        locale={locale}
        userName={authUser?.name || ""}
        profileComplete={guideProfileComplete}
        hasTrustedContact={guideTrustedContactCount > 0}
        onOpenFamilyChart={openFamilyChartGuideTarget}
        onWriteJournal={openJournalGuideTarget}
        onOpenMediaGallery={openPhotoMemoryGallery}
        onReviewVerification={openVerificationGuideTarget}
      />
    ) : null}

    <CookieConsent copy={publicCopy.cookie} />
    </>
  );
}

export default App;
